import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Coins, Gift, Copy, ChevronRight, ChevronLeft, Trophy, Star, Clock,
  Target, Wallet, CheckCircle, Lock, Share2, Users, Tag, Heart, Eye, History,
  TrendingUp, TrendingDown, Package, Award, RefreshCw, CalendarDays, Percent,
  ListChecks, Upload, Timer, ArrowUpRight, LayoutDashboard, UserCheck,
  Bell, Mail, LogOut, Settings, Camera, Check, Globe, Sun, Moon, Sparkles,
  ShoppingCart, Inbox, Headphones, Edit2, X, MessageCircle, User,
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import OnboardingTour from "../components/OnboardingTour";
import OnboardingSurvey from "../components/OnboardingSurvey";
import Lightbox from "../components/Lightbox";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "../components/ui/dialog";
import { formatIsraelDateTime } from "../lib/time";
import { L, t } from "../lib/translations";
import {
  canonicalizeInstagramPostUrl, canonicalizeInstagramProfileUrl,
  isValidInstagramPostUrl, isValidInstagramProfileUrl,
} from "../lib/instagram";

const API     = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const DASHBOARD_TAB_IDS = ["overview", "orders", "topups", "tasks", "rewards", "settings"];



const fmtIp = (n) => {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? v.toLocaleString() : v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/* ── Status helpers ── */
const orderStatusLabel = (lang, s) => ({ sent: t("Admin.sent", lang), processing: t("Admin.processing", lang), completed: t("Admin.completed", lang), cancelled: t("DashboardV2.cancelled", lang) }[s] || t("Admin.sent", lang));
const orderStatusTone  = (s) => ({ sent: { color: "#7aa2ff" }, processing: { color: "#F77737" }, completed: { color: "#22c55e" }, cancelled: { color: "#ef4444" } }[s] || { color: "#7aa2ff" });
const topupStatusLabel = (lang, s) => ({ draft: t("Admin.draft", lang), pending: t("Admin.pending", lang), approved: t("order.approved", lang), rejected: t("order.rejected", lang) }[s] || t("Admin.pending", lang));
const topupStatusTone  = (s) => ({ draft: { color: "#94a3b8" }, pending: { color: "#F77737" }, approved: { color: "#22c55e" }, rejected: { color: "#ef4444" } }[s] || { color: "#F77737" });

const formatDT  = (v) => formatIsraelDateTime(v);
const resolveUrl = (url) => (!url ? "" : url.startsWith("http") ? url : `${BACKEND}${url}`);

const orderItemsSummary = (lang, items) => {
  if (!Array.isArray(items) || items.length === 0) return t("DashboardV2.order_details", lang);
  return items.map(i => `${i?.product_name?.[lang] || i?.product_name?.en || t("Admin.service", lang)} ×${i?.quantity ?? 0}`).join(", ");
};

const compactLink = (v) => {
  const raw = String(v || "").trim(); if (!raw) return "";
  try { const u = new URL(raw); return `${u.host}${u.pathname.replace(/\/$/, "")}`; } catch { return raw; }
};

const getTopupBonusTotal = (t) => Number(t?.large_topup_bonus||0) + Number(t?.promo_bonus||0) + Number(t?.global_bonus||0);

/* ── Task platform icons ── */
function TaskPlatformIcon({ type, color, size = 14 }) {
  const s = { width: size, height: size, fill: "currentColor", color, flexShrink: 0 };
  switch (type) {
    case "instagram_follow": case "instagram_like":
      return <svg viewBox="0 0 24 24" style={s}><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>;
    case "telegram":
      return <svg viewBox="0 0 24 24" style={s}><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>;
    default:
      return <ListChecks style={s} />;
  }
}

const TASK_META = {
  instagram_follow: { label: { en:"Instagram", ru:"Instagram", he:"אינסטגרם" }, color:"#E1306C" },
  instagram_like:   { label: { en:"Instagram", ru:"Instagram", he:"אינסטגרם" }, color:"#F77737" },
  telegram:         { label: { en:"Telegram",  ru:"Telegram",  he:"טלגרם"    }, color:"#0088CC" },
  tiktok:           { label: { en:"TikTok",    ru:"TikTok",    he:"טיקטוק"   }, color:"#EE1D52" },
  youtube:          { label: { en:"YouTube",   ru:"YouTube",   he:"יוטיוב"   }, color:"#FF0000" },
  twitter:          { label: { en:"X/Twitter", ru:"X/Twitter", he:"טוויטר"   }, color:"#1A8CD8" },
  website:          { label: { en:"Website",   ru:"Сайт",      he:"אתר"      }, color:"#405DE6" },
  custom:           { label: { en:"Task",      ru:"Задание",   he:"משימה"    }, color:"#833AB4" },
};

/* ── iPoints ledger config ── */
const IP_SOURCE_CFG = {
  topup:       { icon: Wallet,      color:"#22c55e", label:{ en:"Top-up",      ru:"Пополнение",    he:"טעינה"   } },
  order:       { icon: Package,     color:"#E1306C", label:{ en:"Order",       ru:"Заказ",         he:"הזמנה"   } },
  task:        { icon: Target,      color:"#405DE6", label:{ en:"Task",        ru:"Задание",       he:"משימה"   } },
  refund:      { icon: RefreshCw,   color:"#22c55e", label:{ en:"Refund",      ru:"Возврат",       he:"החזר"    } },
  daily:       { icon: CalendarDays,color:"#833AB4", label:{ en:"Daily",       ru:"Ежедневный",    he:"יומי"    } },
  referral:    { icon: Users,       color:"#405DE6", label:{ en:"Referral",    ru:"Реферал",       he:"הפניה"   } },
  promo:       { icon: Percent,     color:"#E1306C", label:{ en:"Promo",       ru:"Промокод",      he:"קופון"   } },
  admin:       { icon: Star,        color:"#F77737", label:{ en:"Admin",       ru:"Администратор", he:"מנהל"    } },
};

/* ── Order detail metric card ── */
function MetricRow({ label, value, color }) {
  return (
    <div className="flex items-center justify-between py-2 border-b last:border-b-0" style={{ borderColor:"var(--border-color)" }}>
      <span className="text-xs" style={{ color:"var(--text-secondary)" }}>{label}</span>
      <span className="text-sm font-bold" style={{ color: color || "var(--text-primary)" }}>{value}</span>
    </div>
  );
}

/* ── TopUp Details Dialog ── */
function TopUpDetailsDialog({ topup, lang, onOpenChange, receiptEnabled }) {
  const tone = topupStatusTone(topup?.status);
  const totalBonus = getTopupBonusTotal(topup);
  const [proofLb, setProofLb] = useState(false);
  const showDownload = receiptEnabled && topup?.status === "approved";

  return (
    <Dialog open={Boolean(topup)} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg overflow-hidden p-0" hideClose
        style={{ background:"var(--bg-secondary)", border:"1px solid var(--border-color)" }}>
        {topup && (
          <div className="max-h-[88vh] overflow-y-auto">
            {/* Header */}
            <div className="px-5 py-4 border-b" style={{ borderColor:"var(--border-color)" }}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <DialogTitle className="text-base font-black" style={{ color:"var(--text-primary)" }}>
                    {t("DashboardV2.top_up", lang)}{topup.ref_code ? ` · ${topup.ref_code}` : ""}
                  </DialogTitle>
                  <p className="text-xs mt-0.5" style={{ color:"var(--text-secondary)" }}>{formatDT(topup.created_at)}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full"
                    style={{ background:`${tone.color}14`, color:tone.color, border:`1px solid ${tone.color}28` }}>
                    {topupStatusLabel(lang, topup.status)}
                  </span>
                  <DialogClose asChild>
                    <button className="flex items-center justify-center w-7 h-7 rounded-lg hover:opacity-70 transition-opacity" style={{ color:"var(--text-secondary)", background:"var(--surface-2)" }}>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                    </button>
                  </DialogClose>
                </div>
              </div>
            </div>

            <div className="p-5 space-y-4">
              {/* Amounts */}
              <div className="flex items-center gap-5 px-1">
                <div>
                  <div className="text-[10px] uppercase tracking-wider mb-0.5" style={{ color:"var(--text-secondary)" }}>{t("DashboardV2.paid", lang)}</div>
                  <div className="text-xl font-black font-outfit" style={{ color:"#22c55e" }}>{Number(topup.amount_ils||0).toLocaleString()} ₪</div>
                </div>
                <div className="w-px h-8 flex-shrink-0" style={{ background:"var(--border-color)" }} />
                <div>
                  <div className="text-[10px] uppercase tracking-wider mb-0.5" style={{ color:"var(--text-secondary)" }}>{t("DashboardV2.receive", lang)}</div>
                  <div className="text-xl font-black font-outfit" style={{ color:"#F77737" }}>{fmtIp(topup.ipoints_amount||0)} iP</div>
                </div>
              </div>

              {/* Bonus breakdown */}
              <div className="rounded-2xl border overflow-hidden" style={{ borderColor:"var(--border-color)", background:"var(--surface-1)" }}>
                <div className="px-4 py-2.5 border-b font-bold text-sm" style={{ borderColor:"var(--border-color)", color:"var(--text-primary)" }}>
                  {t("DashboardV2.bonus_breakdown", lang)}
                </div>
                <div className="px-4 py-2">
                  <MetricRow label={t("DashboardV2.base", lang)} value={`${fmtIp(topup.base_ipoints||0)} iP`} color="#7aa2ff" />
                  {(topup.large_topup_bonus||0) > 0 && <MetricRow label={t("DashboardV2.large_top_up_bonus", lang)} value={`+${Number(topup.large_topup_bonus).toLocaleString()} iP${topup.large_topup_bonus_pct ? ` (${topup.large_topup_bonus_pct}%)` : ""}`} color="#22c55e" />}
                  {(topup.promo_bonus||0) > 0 && <MetricRow label={t("DashboardV2.promo_bonus", lang)} value={`+${Number(topup.promo_bonus).toLocaleString()} iP${topup.promo_code ? ` · ${topup.promo_code}` : ""}`} color="#E1306C" />}
                  <div className="flex items-center justify-between pt-2 mt-1">
                    <span className="text-sm font-semibold" style={{ color:"var(--text-secondary)" }}>{t("DashboardV2.total_bonus", lang)}</span>
                    <span className="text-base font-black" style={{ color:"#F77737" }}>+{Number(totalBonus||0).toLocaleString()} iP</span>
                  </div>
                </div>
              </div>

              {/* Details */}
              <div className="rounded-2xl border overflow-hidden" style={{ borderColor:"var(--border-color)", background:"var(--surface-1)" }}>
                <div className="px-4 py-2.5 border-b font-bold text-sm" style={{ borderColor:"var(--border-color)", color:"var(--text-primary)" }}>
                  {t("DashboardV2.payment_details", lang)}
                </div>
                <div className="px-4 py-2">
                  <MetricRow label={t("DashboardV2.reference", lang)} value={topup.ref_code || "—"} />
                  {topup.promo_code && <MetricRow label={t("DashboardV2.promo_code", lang)} value={topup.promo_code} />}
                  <MetricRow label={t("DashboardV2.exchange_rate", lang)} value={`1 ₪ = ${Number(topup.exchange_rate||1).toLocaleString()} iP`} />
                  <MetricRow label={t("DashboardV2.reviewed_at", lang)} value={formatDT(topup.reviewed_at)} />
                  {topup.admin_note && <MetricRow label={t("DashboardV2.admin_note", lang)} value={topup.admin_note} />}
                </div>
              </div>

              {/* Proof */}
              {topup.proof_image && (
                <div>
                  <div className="text-xs font-semibold mb-2" style={{ color:"var(--text-secondary)" }}>{t("DashboardV2.payment_proof", lang)}</div>
                  <button onClick={() => setProofLb(true)} className="block w-full overflow-hidden rounded-2xl cursor-zoom-in border press-card" style={{ borderColor:"var(--border-color)" }}>
                    <img src={resolveUrl(topup.proof_image)} alt="" className="max-h-[260px] w-full object-contain" style={{ background:"var(--surface-2)" }} />
                  </button>
                  {proofLb && <Lightbox src={resolveUrl(topup.proof_image)} onClose={() => setProofLb(false)} />}
                </div>
              )}

              {/* Download invoice */}
              {showDownload && (
                <a
                  href={`${API}/topup/${topup.topup_id}/receipt`}
                  download
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display:"flex", alignItems:"center", justifyContent:"center", gap:8,
                    padding:"11px 0", borderRadius:14, width:"100%",
                    background:"linear-gradient(135deg,#7c3aed,#2563eb)",
                    color:"#fff", fontWeight:700, fontSize:14,
                    textDecoration:"none", boxShadow:"0 2px 12px rgba(124,58,237,0.25)",
                  }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  {lang === "he" ? "הורד חשבונית" : lang === "ru" ? "Скачать инвойс" : "Download Invoice"}
                </a>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ── Order timeline ── */
function OrderTimeline({ order, lang }) {
  const status = order.public_status || "sent";
  const entries = Array.isArray(order?.public_timeline) ? order.public_timeline : [];
  const tMap = Object.fromEntries(entries.map(e => [e.stage, e.occurred_at]));
  const stages = [
    { id:"sent",       done:true,                                                label:t("Admin.sent", lang),          t: tMap.sent || order?.created_at,            color:"#7aa2ff" },
    { id:"processing", done:["processing","completed","cancelled"].includes(status), label:t("DashboardV2.in_progress", lang), t: tMap.processing||"", color:"#F77737" },
    { id:"final",      done:["completed","cancelled"].includes(status),           label: status==="cancelled" ? t("DashboardV2.cancelled", lang) : t("Admin.completed", lang), t: tMap.completed||tMap.cancelled||"", color: status==="cancelled"?"#ef4444":"#22c55e" },
  ];
  return (
    <div className="flex items-center gap-0">
      {stages.map((s, i) => (
        <div key={s.id} className="flex items-center flex-1">
          <div className="flex flex-col items-center">
            <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 border-2"
              style={{ borderColor: s.done ? s.color : "var(--border-color)", background: s.done ? `${s.color}20` : "var(--surface-1)" }}>
              {s.done && <div className="w-2 h-2 rounded-full" style={{ background: s.color }} />}
            </div>
            <div className="text-[10px] mt-1 text-center max-w-[64px]" style={{ color: s.done ? s.color : "var(--text-secondary)" }}>{s.label}</div>
            {s.t && s.done && <div className="text-[9px] mt-0.5 text-center max-w-[72px]" style={{ color:"var(--text-secondary)" }}>{formatDT(s.t).split(",")[0]}</div>}
          </div>
          {i < stages.length - 1 && (
            <div className="flex-1 h-px mx-1 mb-5" style={{ background: stages[i+1].done ? stages[i+1].color+"44" : "var(--border-color)" }} />
          )}
        </div>
      ))}
    </div>
  );
}

/* ── Order Details Dialog ── */
function OrderDetailsDialog({ order, lang, onOpenChange }) {
  const status = order?.public_status || "sent";
  const tone = orderStatusTone(status);
  const items = Array.isArray(order?.items) ? order.items : [];

  return (
    <Dialog open={Boolean(order)} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg overflow-hidden p-0" hideClose
        style={{ background:"var(--bg-secondary)", border:"1px solid var(--border-color)" }}>
        {order && (
          <div className="max-h-[88vh] overflow-y-auto">
            {/* Header */}
            <div className="px-5 py-4 border-b" style={{ borderColor:"var(--border-color)" }}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <DialogTitle className="text-base font-black" style={{ color:"var(--text-primary)" }}>
                    {t("Admin.order_2", lang)} #{order.order_number}
                  </DialogTitle>
                  <p className="text-xs mt-0.5 truncate" style={{ color:"var(--text-secondary)" }}>{orderItemsSummary(lang, order.items)}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="flex flex-col items-end gap-1">
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full"
                      style={{ background:`${tone.color}14`, color:tone.color, border:`1px solid ${tone.color}28` }}>
                      {orderStatusLabel(lang, status)}
                    </span>
                    {order?.provider_sync_status === "partial" && (
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full"
                        style={{ background:"rgba(168,85,247,0.12)", color:"#a855f7", border:"1px solid rgba(168,85,247,0.22)" }}>
                        {t("DashboardV2.partial_delivery", lang)}
                      </span>
                    )}
                  </div>
                  <DialogClose asChild>
                    <button className="flex items-center justify-center w-7 h-7 rounded-lg press-sm flex-shrink-0" style={{ color:"var(--text-secondary)", background:"var(--surface-2)" }}>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                    </button>
                  </DialogClose>
                </div>
              </div>
            </div>

            <div className="p-5 space-y-4">
              {/* Stats */}
              <div className="flex items-center gap-4 px-1">
                {[
                  { label:t("DashboardV2.spent", lang),    value: order.total_ipoints > 0 ? `${order.total_ipoints} iP` : t("Admin.free", lang), color:"#F77737" },
                  { label:t("DashboardV2.products", lang), value: items.length, color:"#7aa2ff" },
                  { label:t("order.date", lang),         value: formatDT(order.created_at).split(",")[0], color:"var(--text-primary)" },
                ].map((s, i) => (
                  <div key={s.label} className="flex items-center gap-2">
                    {i > 0 && <span className="w-px h-4 flex-shrink-0" style={{ background:"var(--border-color)" }} />}
                    <span className="text-sm font-black font-outfit" style={{ color:s.color }}>{s.value}</span>
                    <span className="text-xs" style={{ color:"var(--text-secondary)" }}>{s.label}</span>
                  </div>
                ))}
              </div>

              {/* Timeline */}
              <div className="rounded-2xl border p-4" style={{ borderColor:"var(--border-color)", background:"var(--surface-1)" }}>
                <div className="text-xs font-semibold mb-3" style={{ color:"var(--text-secondary)" }}>{t("DashboardV2.order_timeline", lang)}</div>
                <OrderTimeline order={order} lang={lang} />
              </div>

              {/* Items */}
              <div className="rounded-2xl border overflow-hidden" style={{ borderColor:"var(--border-color)", background:"var(--surface-1)" }}>
                <div className="px-4 py-2.5 border-b font-bold text-sm" style={{ borderColor:"var(--border-color)", color:"var(--text-primary)" }}>
                  {t("DashboardV2.what_you_ordered", lang)}
                </div>
                <div className="divide-y" style={{ borderColor:"var(--border-color)" }}>
                  {items.map((item, idx) => {
                    const pOrders = Array.isArray(order.provider_orders) ? order.provider_orders : [];
                    const prov = pOrders[idx] || {};
                    const qty = Number(item?.quantity);
                    const remains = Number(prov.provider_remains);
                    const delivered = !isNaN(qty) && !isNaN(remains) ? Math.max(0, qty - remains) : null;
                    const accent = item.category === "followers" ? "#833AB4" : "#E1306C";
                    const name = item.product_name?.[lang] || item.product_name?.en || t("Admin.service", lang);
                    return (
                      <div key={idx} className="px-4 py-3">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-2 h-2 rounded-full flex-shrink-0 mt-0.5" style={{ background:accent }} />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold truncate" style={{ color:"var(--text-primary)" }}>{name}</div>
                            {item.instagram_link && (
                              <a href={item.instagram_link} target="_blank" rel="noopener noreferrer"
                                className="text-[11px] truncate block hover:opacity-70" style={{ color:"#7aa2ff" }}>
                                {compactLink(item.instagram_link)}
                              </a>
                            )}
                          </div>
                          <span className="text-sm font-black flex-shrink-0" style={{ color:accent }}>×{qty?.toLocaleString()}</span>
                        </div>
                        {(delivered !== null || !isNaN(Number(prov.provider_start_count))) && (
                          <div className="flex items-center gap-3 flex-wrap pt-1">
                            {!isNaN(Number(prov.provider_start_count)) && (
                              <span className="text-[11px]">
                                <span style={{ color:"var(--text-secondary)" }}>{t("DashboardV2.start", lang)}: </span>
                                <span className="font-bold" style={{ color:"#7aa2ff" }}>{Number(prov.provider_start_count).toLocaleString()}</span>
                              </span>
                            )}
                            {delivered !== null && (
                              <span className="text-[11px]">
                                <span style={{ color:"var(--text-secondary)" }}>{t("DashboardV2.delivered", lang)}: </span>
                                <span className="font-bold" style={{ color:"#22c55e" }}>{delivered.toLocaleString()}</span>
                              </span>
                            )}
                            {!isNaN(remains) && (
                              <span className="text-[11px]">
                                <span style={{ color:"var(--text-secondary)" }}>{t("DashboardV2.left", lang)}: </span>
                                <span className="font-bold" style={{ color:"#F77737" }}>{remains.toLocaleString()}</span>
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {status === "cancelled" && (
                <div className="flex items-start gap-2 px-1 pt-1">
                  <Eye className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color:"#F77737" }} />
                  <p className="text-xs leading-5" style={{ color:"var(--text-secondary)" }}>
                    {t("DashboardV2.your_ipoints_were_returned_to_balance_if_the_pro", lang)}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ── Empty state ── */
function EmptyState({ icon, title, description, actionTo, actionLabel }) {
  return (
    <div className="rounded-2xl border p-10 text-center" style={{ borderColor:"var(--border-color)", background:"var(--surface-1)" }}>
      <div className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-3"
        style={{ background:"var(--surface-2)", color:"var(--text-secondary)" }}>
        {icon}
      </div>
      <p className="font-bold text-sm mb-1" style={{ color:"var(--text-primary)" }}>{title}</p>
      <p className="text-xs" style={{ color:"var(--text-secondary)" }}>{description}</p>
      {actionTo && (
        <Link to={actionTo} className="inline-flex items-center gap-1 mt-4 text-xs font-semibold px-3 py-1.5 rounded-xl press-sm"
          style={{ background:"#8B5CF6", color:"#fff" }}>
          {actionLabel}
        </Link>
      )}
    </div>
  );
}

/* ── iPoints History ── */
function IPointsHistory({ ipHistory, lang }) {
  const totalEarned = ipHistory.filter(e => e.delta > 0).reduce((s, e) => s + e.delta, 0);
  const totalSpent  = ipHistory.filter(e => e.delta < 0).reduce((s, e) => s + Math.abs(e.delta), 0);

  const relTime = (iso) => {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000), hrs = Math.floor(diff / 3600000), days = Math.floor(diff / 86400000);
    if (mins < 2)  return t("DashboardV2.just_now", lang);
    if (mins < 60) return L(lang, `${mins}m ago`, `${mins} мин`, mins === 1 ? "לפני דקה"  : `לפני ${mins} דקות`);
    if (hrs < 24)  return L(lang, `${hrs}h ago`,  `${hrs} ч`,   hrs  === 1 ? "לפני שעה"  : `לפני ${hrs} שעות`);
    if (days < 7)  return L(lang, `${days}d ago`, `${days} дн`, days === 1 ? "לפני יום"  : `לפני ${days} ימים`);
    return formatDT(iso).split(",")[0];
  };

  return (
    <div className="rounded-2xl border overflow-hidden" style={{ borderColor:"var(--border-color)", background:"var(--surface-1)" }}>
      <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor:"var(--border-color)" }}>
        <span className="font-bold text-sm" style={{ color:"var(--text-primary)" }}>{t("Admin.ipoints_history", lang)}</span>
        <div className="flex items-center gap-3 text-xs">
          <span style={{ color:"#22c55e" }}>+{fmtIp(totalEarned)}</span>
          <span style={{ color:"#ef4444" }}>−{fmtIp(totalSpent)}</span>
        </div>
      </div>
      {ipHistory.length === 0 ? (
        <div className="p-6 text-center text-xs" style={{ color:"var(--text-secondary)" }}>{t("DashboardV2.no_history_yet", lang)}</div>
      ) : (
        <div className="divide-y" style={{ borderColor:"var(--border-color)" }}>
          {ipHistory.slice(0, 15).map((entry) => {
            const cfg = IP_SOURCE_CFG[entry.source_type] || IP_SOURCE_CFG.admin;
            const Icon = cfg.icon;
            const isGain = entry.delta > 0;
            const label = entry.label?.[lang] || entry.label?.en || entry.source_type;
            return (
              <div key={entry.ledger_id || `${entry.source_type}-${entry.created_at}`}
                className="flex items-center gap-3 px-4 py-3">
                <Icon className="w-4 h-4 flex-shrink-0" style={{ color:cfg.color }} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate" style={{ color:"var(--text-primary)" }}>{label}</div>
                  <div className="text-[11px]" style={{ color:"var(--text-secondary)" }}>{relTime(entry.created_at)}</div>
                </div>
                <span className="text-sm font-black font-outfit flex-shrink-0" style={{ color: isGain ? "#22c55e" : "#ef4444" }}>
                  {isGain ? "+" : "−"}{fmtIp(Math.abs(entry.delta))}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   MAIN DASHBOARD COMPONENT
══════════════════════════════════════════════ */
export default function DashboardV2() {
  const { lang, setLang } = useLanguage();
  const { user, setUser, refreshUser, logout } = useAuth();
  const [searchParams]   = useSearchParams();
  const isRTL            = lang === "he";

  const [orders,       setOrders]       = useState([]);
  const [ordersPage,   setOrdersPage]   = useState(0);
  const [selectedOrder,setSelectedOrder]= useState(null);
  const [topups,       setTopups]       = useState([]);
  const [topupsPage,   setTopupsPage]   = useState(0);
  const [selectedTopup,setSelectedTopup]= useState(null);
  const [tasks,        setTasks]        = useState([]);
  const [uploading,    setUploading]    = useState(null);
  const [activeTab,    setActiveTab]    = useState(() => {
    const t = searchParams.get("tab");
    if (DASHBOARD_TAB_IDS.includes(t)) return t;
    return "overview";
  });
  const [openSections, setOpenSections] = useState({ orders: true, topups: false, tasks: false, rewards: false });
  const toggleSection = (key) => setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  const [giftStatus,    setGiftStatus]   = useState(null);
  const [giftBlockSecs, setGiftBlockSecs]= useState({ likes:0, followers:0 });
  const [giftPopupOpen, setGiftPopupOpen]= useState(false);
  const [referralStats, setReferralStats]= useState(null);
  const [myReferrer,    setMyReferrer]   = useState(null);
  const [likesLink,     setLikesLink]    = useState("");
  const [likesLinkTouched, setLikesLinkTouched] = useState(false);
  const [followersLink, setFollowersLink]= useState("");
  const [followersLinkTouched, setFollowersLinkTouched] = useState(false);
  const [loading,       setLoading]      = useState(true);
  const [showPromo,     setShowPromo]    = useState(false);
  const [promoInput,    setPromoInput]   = useState("");
  const [promoResult,   setPromoResult]  = useState(null);
  const [showRefInput,  setShowRefInput] = useState(false);
  const [refInput,      setRefInput]     = useState("");
  const [emailPrefs,    setEmailPrefs]   = useState({ order_completed: true, topup_approval: true, tasks: true, global_bonus: true, inbox_messages: true, support_replies: true });
  const [emailPrefsSaving, setEmailPrefsSaving] = useState(false);
  const [receiptEnabled, setReceiptEnabled] = useState(false);


  const [editName, setEditName]       = useState("");
  const [editingName, setEditingName] = useState(false);
  const [nameSaving, setNameSaving]   = useState(false);
  const [pictureSaving, setPictureSaving] = useState(false);
  const pictureInputRef = useRef(null);
  const [pwForm, setPwForm]           = useState({ current: "", next: "", confirm: "" });
  const [pwSaving, setPwSaving]       = useState(false);
  const [pwOpen, setPwOpen]           = useState(false);

  const [theme, setThemeState] = useState(() => {
    try { const s = localStorage.getItem("a11y_settings"); if (s) return JSON.parse(s).theme || "dark"; } catch {} return "dark";
  });
  const applyTheme = (next) => {
    setThemeState(next);
    if (next === "light") document.documentElement.classList.add("theme-light");
    else document.documentElement.classList.remove("theme-light");
    try {
      const saved = localStorage.getItem("a11y_settings");
      const settings = saved ? JSON.parse(saved) : {};
      settings.theme = next;
      localStorage.setItem("a11y_settings", JSON.stringify(settings));
    } catch {}
    window.dispatchEvent(new Event("theme-change"));
  };

  const [notifPrefs, setNotifPrefs] = useState(() => {
    try { return JSON.parse(localStorage.getItem("notif_prefs") || "{}"); } catch { return {}; }
  });
  const toggleNotifPref = (key) => {
    const next = { ...notifPrefs, [key]: !(notifPrefs[key] !== false) };
    setNotifPrefs(next);
    try { localStorage.setItem("notif_prefs", JSON.stringify(next)); } catch {}
  };
  const [showCompletedHistory, setShowCompletedHistory] = useState(false);
  const [showWelcome,   setShowWelcome]  = useState(false);
  const [showSurvey, setShowSurvey] = useState(false);
  const surveyCheckedRef = useRef(false);
  const [ipHistory,     setIpHistory]    = useState([]);
  const [gravatarUrl,   setGravatarUrl]  = useState(null);
  const latestOrderAutoOpenedRef = useRef(false);

  /* ── Data fetch ── */
  const fetch_ = useCallback(async () => {
    try {
      const [ord, topupHistory, gift, refInfo, ledger, tasksData, pub] = await Promise.all([
        fetch(`${API}/orders`, { credentials:"include" }).then(r => r.ok ? r.json() : []),
        fetch(`${API}/topup/history`, { credentials:"include" }).then(r => r.ok ? r.json() : []),
        fetch(`${API}/free-gift/status`, { credentials:"include" }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/referral`, { credentials:"include" }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/ipoints/history`, { credentials:"include" }).then(r => r.ok ? r.json() : []),
        fetch(`${API}/tasks`, { credentials:"include" }).then(r => r.ok ? r.json() : []),
        fetch(`${API}/site/settings-public`).then(r => r.ok ? r.json() : {}),
      ]);
      setOrders(Array.isArray(ord) ? ord : []);
      setTopups(Array.isArray(topupHistory) ? topupHistory : []);
      setReceiptEnabled(!!(pub || {}).receipt_enabled);
      setGiftStatus(gift);
      setReferralStats(refInfo);
      setIpHistory(Array.isArray(ledger) ? ledger : []);
      setTasks(Array.isArray(tasksData) ? tasksData : []);
      if (user?.email_notifications) {
        setEmailPrefs(p => ({ ...p, ...user.email_notifications }));
      }
      if (user?.referred_by) {
        fetch(`${API}/referral/my-referrer`, { credentials:"include" }).then(r => r.ok ? r.json() : null).then(setMyReferrer).catch(() => {});
      }

      const pendingRef = localStorage.getItem("pending_ref");
      if (!user?.referred_by && pendingRef) {
        localStorage.removeItem("pending_ref");
        fetch(`${API}/referral/apply`, { method:"POST", headers:{"Content-Type":"application/json"}, credentials:"include", body:JSON.stringify({ code:pendingRef }) })
          .then(r => r.json()).then(data => { if (data.message) { toast.success(data.message); refreshUser(); } }).catch(() => {});
      }
      if (!user?.referred_by && !pendingRef && !localStorage.getItem("promo_shown")) {
        setShowPromo(true); localStorage.setItem("promo_shown","1");
      }
    } catch {} finally { setLoading(false); }
  }, [refreshUser, user?.referred_by]);


  useEffect(() => { fetch_(); const iv = setInterval(fetch_, 30000); const onLive = () => fetch_(); window.addEventListener("ifollowers:live", onLive); return () => { clearInterval(iv); window.removeEventListener("ifollowers:live", onLive); }; }, [fetch_]);
  useEffect(() => { setSelectedOrder(c => !c ? null : orders.find(o => o.order_id === c.order_id) || null); }, [orders]);
  useEffect(() => { setSelectedTopup(c => !c ? null : topups.find(t => t.topup_id === c.topup_id) || null); }, [topups]);
  useEffect(() => { const t = searchParams.get("tab"); if (DASHBOARD_TAB_IDS.includes(t)) setActiveTab(t); }, [searchParams]);
  useEffect(() => {
    if (searchParams.get("order") !== "latest") { latestOrderAutoOpenedRef.current = false; return; }
    if (orders.length === 0 || latestOrderAutoOpenedRef.current) return;
    setActiveTab("orders"); setSelectedOrder(orders[0]); latestOrderAutoOpenedRef.current = true;
  }, [orders, searchParams]);
  useEffect(() => { setOrdersPage(p => Math.min(p, Math.max(0, Math.ceil(orders.length / 6) - 1))); }, [orders.length]);
  useEffect(() => { setTopupsPage(p => Math.min(p, Math.max(0, Math.ceil(topups.length / 6) - 1))); }, [topups.length]);
  useEffect(() => {
    if (!giftStatus) return;
    const compute = () => { const now = Date.now(); const secsLeft = iso => iso ? Math.max(0, Math.ceil((new Date(iso) - now) / 1000)) : 0; setGiftBlockSecs({ likes: secsLeft(giftStatus.likes_blocked_until), followers: secsLeft(giftStatus.followers_blocked_until) }); };
    compute(); const iv = setInterval(compute, 1000); return () => clearInterval(iv);
  }, [giftStatus]);
  useEffect(() => {
    if (!giftStatus) return;
    const mc = Math.max(giftStatus.likes_reject_count||0, giftStatus.followers_reject_count||0);
    if (mc >= 3 && !localStorage.getItem(`gift_reject_popup_${mc}`)) setGiftPopupOpen(true);
  }, [giftStatus]);
  useEffect(() => {
    if (user?.picture || !user?.email) { setGravatarUrl(null); return; }
    (async () => { try { const enc = new TextEncoder().encode(user.email.trim().toLowerCase()); const buf = await crypto.subtle.digest("SHA-256", enc); const hex = Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2,"0")).join(""); setGravatarUrl(`https://www.gravatar.com/avatar/${hex}?s=120&d=identicon&r=pg`); } catch { setGravatarUrl(null); } })();
  }, [user?.email, user?.picture]);

  // Check tour + survey status from server for all users
  useEffect(() => {
    if (!user?.user_id || surveyCheckedRef.current) return;
    surveyCheckedRef.current = true;
    (async () => {
      try {
        // Check if tour was done (server-side)
        if (!user.onboarding_tour_done) {
          setShowWelcome(true);
          return; // survey will show after tour
        }
        // Tour done — check survey
        const r = await fetch(`${API}/onboarding/survey`, { credentials: "include" });
        if (r.ok) {
          const d = await r.json();
          if (!d || d.status === "not_answered") setShowSurvey(true);
        }
      } catch (_) {}
    })();
  }, [user?.user_id, user?.onboarding_tour_done]);

  const fmtBlockTime = (s) => { const m = Math.floor(s/60), sec = s%60; return m > 0 ? `${m}:${String(sec).padStart(2,"0")}` : `${sec}s`; };

  const claimGift = async (type, link) => {
    const norm = type === "likes" ? canonicalizeInstagramPostUrl(link) : canonicalizeInstagramProfileUrl(link);
    if (type === "likes") { setLikesLinkTouched(true); if (!link.trim()) return toast.error(t("DashboardV2.enter_post_link", lang)); if (!isValidInstagramPostUrl(norm||link)) return toast.error(t("DashboardV2.enter_a_valid_post_link", lang)); if (norm) setLikesLink(norm); }
    else { setFollowersLinkTouched(true); if (!link.trim()) return toast.error(t("DashboardV2.enter_profile_link", lang)); if (!isValidInstagramProfileUrl(norm||link)) return toast.error(t("DashboardV2.enter_a_valid_profile_link", lang)); if (norm) setFollowersLink(norm); }
    try {
      const r = await fetch(`${API}/free-gift/claim-${type}`, { method:"POST", headers:{"Content-Type":"application/json"}, credentials:"include", body:JSON.stringify({ instagram_link: norm||link }) });
      const d = await r.json(); if (!r.ok) throw new Error(d.detail);
      toast.success(d.message); fetch_(); refreshUser();
    } catch (e) { toast.error(e.message || t("DashboardV2.something_went_wrong_try_again", lang)); }
  };

  const toggleEmailPref = async (key) => {
    const next = { ...emailPrefs, [key]: !emailPrefs[key] };
    setEmailPrefs(next);
    setEmailPrefsSaving(true);
    try {
      await fetch(`${API}/user/email-notifications`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [key]: next[key] }),
      });
    } catch { toast.error(t("DashboardV2.could_not_save_preferences_try_again", lang)); setEmailPrefs(emailPrefs); }
    finally { setEmailPrefsSaving(false); }
  };

  const saveName = async () => {
    const trimmed = editName.trim();
    if (!trimmed) { toast.error(t("DashboardV2.name_cannot_be_empty", lang)); return; }
    if (trimmed === (user?.name || "")) { setEditingName(false); return; }
    setNameSaving(true);
    try {
      const r = await fetch(`${API}/user/profile`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: trimmed }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Failed");
      await refreshUser();
      toast.success(t("DashboardV2.name_updated", lang));
      setEditingName(false);
    } catch (e) { toast.error(e.message); }
    finally { setNameSaving(false); }
  };

  const uploadPicture = async (file) => {
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/jpg"].includes(file.type)) {
      toast.error(L(lang, "Only PNG or JPG files are allowed", "Разрешены только PNG и JPG", "מותרים רק PNG או JPG"));
      return;
    }
    if (file.size > 5 * 1024 * 1024) { toast.error(t("DashboardV2.file_too_large_max_5mb", lang)); return; }
    setPictureSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await fetch(`${API}/upload`, { method: "POST", credentials: "include", body: fd });
      const upData = await up.json();
      if (!up.ok) throw new Error(upData.detail || "Upload failed");
      const pictureUrl = `${BACKEND}${upData.url}`;
      const r = await fetch(`${API}/user/profile`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ picture: pictureUrl }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Failed");
      await refreshUser();
      toast.success(t("DashboardV2.photo_updated", lang));
    } catch (e) { toast.error(e.message); }
    finally { setPictureSaving(false); }
  };

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    try {
      const r = await fetch(`${API}/promo-codes/apply`, { method:"POST", headers:{"Content-Type":"application/json"}, credentials:"include", body:JSON.stringify({ code:promoInput }) });
      const d = await r.json(); if (!r.ok) throw new Error(d.detail);
      setPromoResult(d.type === "fixed" ? { tone:"success", text:d.message } : { tone:"info", text:t("DashboardV2.this_promo_gives_a_bonus_on_your_next_top_up", lang) });
      toast.success(d.message); setShowPromo(false); setPromoInput(""); refreshUser();
    } catch (e) { toast.error(e.message || t("DashboardV2.invalid_promo_code", lang)); }
  };

  const applyReferral = async () => {
    if (!refInput.trim()) return;
    try {
      const r = await fetch(`${API}/referral/apply`, { method:"POST", headers:{"Content-Type":"application/json"}, credentials:"include", body:JSON.stringify({ code:refInput }) });
      const d = await r.json(); if (!r.ok) throw new Error(d.detail);
      toast.success(d.message); setShowRefInput(false); setRefInput(""); refreshUser(); fetch_();
    } catch (e) { toast.error(e.message || t("DashboardV2.invalid_referral_code", lang)); }
  };

  const copyRef = async () => {
    const link = `${window.location.origin}?ref=${user?.referral_code}`;
    const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
    if (isMobile && navigator.share) {
      const shareText = lang === "he"
        ? `🚀 גלית את iFollowers?\nאני מגדל שם את האינסטגרם שלי עם עוקבים, לייקים וצפיות אמיתיות — מהיר, בטוח ובמחיר נהדר!\nהצטרף עכשיו עם הלינק שלי וקבל בונוס כניסה 🎁\n${link}`
        : lang === "ru"
        ? `🚀 Уже знаешь iFollowers?\nЯ раскручиваю свой Instagram там — подписчики, лайки и просмотры, быстро и безопасно!\nРегистрируйся по моей ссылке и получи приветственный бонус 🎁\n${link}`
        : `🚀 Have you tried iFollowers?\nI'm growing my Instagram there — real followers, likes & views, fast & affordable!\nSign up with my link and get a welcome bonus 🎁\n${link}`;
      try {
        await navigator.share({ text: shareText });
        toast.success(t("DashboardV2.shared", lang));
        return;
      } catch (e) {
        if (e.name === "AbortError") return;
      }
    }
    await navigator.clipboard.writeText(link);
    toast.success(t("DashboardV2.link_copied", lang));
  };

  const uploadProof = async (taskId, file) => {
    setUploading(taskId);
    try {
      const fd = new FormData(); fd.append("file", file);
      const uploadRes = await fetch(`${API}/upload`, { method:"POST", body:fd, credentials:"include" });
      if (!uploadRes.ok) throw new Error(t("DashboardV2.upload_failed_try_again", lang));
      const { url } = await uploadRes.json();
      const r = await fetch(`${API}/tasks/${taskId}/submit`, { method:"POST", headers:{"Content-Type":"application/json"}, credentials:"include", body:JSON.stringify({ proof_image:url }) });
      const d = await r.json(); if (!r.ok) throw new Error(d.detail);
      toast.success(t("DashboardV2.proof_submitted", lang));
      fetch_();
    } catch (e) { toast.error(e.message || t("DashboardV2.something_went_wrong_try_again", lang)); } finally { setUploading(null); }
  };

  const getTimeLeft = (expiresAt) => {
    if (!expiresAt) return null;
    const diff = new Date(expiresAt) - new Date();
    if (diff <= 0) return t("DashboardV2.expired", lang);
    const h = Math.floor(diff/3600000), m = Math.floor((diff%3600000)/60000);
    if (h > 24) return `${Math.floor(h/24)}${t("Admin.d_2", lang)} ${h%24}${t("Admin.h", lang)}`;
    if (h > 0)  return `${h}${t("Admin.h", lang)} ${m}${t("DashboardV2.m", lang)}`;
    return `${m}${t("DashboardV2.m", lang)}`;
  };

  /* ── Derived values ── */
  const firstName       = typeof user?.name === "string" && user.name.trim() ? user.name.trim().split(/\s+/)[0] : t("DashboardV2.friend", lang);
  const approvedTopups  = topups.filter(t => t.status === "approved");
  const totalApprovedIls= approvedTopups.reduce((s, t) => s + Number(t.amount_ils||0), 0);
  const totalApprovedIp = approvedTopups.reduce((s, t) => s + Number(t.ipoints_amount||0), 0);
  const activeOrders    = orders.filter(o => ["sent","processing"].includes(o.public_status||"sent")).length;
  const completedOrders = orders.filter(o => (o.public_status||"sent") === "completed").length;
  const spentIpoints    = orders.reduce((s, o) => s + Number(o.total_ipoints||0), 0);
  const referralLink    = user?.referral_code ? `${window.location.origin}?ref=${user.referral_code}` : "";
  const pagedOrders     = orders.slice(ordersPage * 6, (ordersPage+1) * 6);
  const pagedTopups     = topups.slice(topupsPage * 6, (topupsPage+1) * 6);
  const availTasks      = tasks.filter(t => !t.my_submissions?.[0]);
  const pendingTasks    = tasks.filter(t => t.my_submissions?.[0]?.status === "pending");
  const doneTasks       = tasks.filter(t => t.my_submissions?.[0]?.status === "approved");
  const activeTasks     = tasks.filter(t => { const s = t.my_submissions?.[0]?.status; return !s || s === "pending" || s === "rejected"; });
  const totalAvailableIp = availTasks.reduce((s, t) => s + Number(t.reward_ipoints || 0), 0);
  const totalEarnedIp   = doneTasks.reduce((s, t) => s + Number(t.reward_ipoints || 0), 0);

  const TABS = [
    { id:"overview", label:t("Admin.overview", lang),       icon:<LayoutDashboard className="w-3.5 h-3.5" /> },
    { id:"orders",   label:t("admin.orders", lang),         icon:<Package className="w-3.5 h-3.5" />,   count:orders.length },
    { id:"topups",   label:t("Admin.top_ups", lang),        icon:<Wallet className="w-3.5 h-3.5" />,    count:topups.length },
    { id:"tasks",    label:t("nav.tasks", lang),            icon:<ListChecks className="w-3.5 h-3.5" />,count:availTasks.length || null },
    { id:"rewards",  label:t("DashboardV2.rewards", lang),  icon:<Trophy className="w-3.5 h-3.5" />,    count:null },
  ];

  if (loading) {
    return (
      <div className="min-h-screen pt-14 flex items-center justify-center">
        <div className="animate-spin h-7 w-7 border-2 rounded-full" style={{ borderColor:"#8B5CF6", borderTopColor:"transparent" }} />
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen pt-14 pb-28" data-testid="dashboard-page">

        {/* ── Promo welcome dialog ── */}
        <Dialog open={showPromo} onOpenChange={setShowPromo}>
          <DialogContent className="max-w-sm" style={{ background:"var(--bg-secondary)", border:"1px solid var(--border-color)" }}>
            <DialogHeader><DialogTitle className="text-center">{t("DashboardV2.welcome", lang)}</DialogTitle></DialogHeader>
            <div className="space-y-3 text-center">
              <p className="text-sm" style={{ color:"var(--text-secondary)" }}>{t("DashboardV2.have_a_promo_or_referral_code", lang)}</p>
              <Input value={promoInput} onChange={e => setPromoInput(e.target.value.toUpperCase())} placeholder={t("DashboardV2.promo_code_2", lang)} className="text-center uppercase font-outfit" data-testid="first-promo-input" />
              <div className="flex gap-2">
                <Button onClick={applyPromo} className="flex-1 h-9 text-sm font-semibold rounded-xl" style={{ background:"#8B5CF6", color:"#fff" }} data-testid="first-promo-apply">{t("dash.apply", lang)}</Button>
                <Button onClick={() => setShowPromo(false)} variant="outline" className="flex-1 h-9 text-sm rounded-xl">{t("DashboardV2.skip", lang)}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <OrderDetailsDialog order={selectedOrder} lang={lang} onOpenChange={open => { if (!open) setSelectedOrder(null); }} />
        <TopUpDetailsDialog topup={selectedTopup} lang={lang} onOpenChange={open => { if (!open) setSelectedTopup(null); }} receiptEnabled={receiptEnabled} />

        {/* Private account popup */}
        {giftPopupOpen && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{ background:"rgba(0,0,0,0.7)" }}>
            <div className="w-full max-w-sm rounded-2xl border p-6 text-center" style={{ borderColor:"rgba(239,68,68,0.3)", background:"var(--bg-secondary)" }}>
              <Lock className="w-8 h-8 mx-auto mb-3 text-[#ef4444]" />
              <h2 className="text-base font-black mb-2" style={{ color:"var(--text-primary)" }}>{t("DashboardV2.account_appears_private", lang)}</h2>
              <p className="text-sm mb-4" style={{ color:"var(--text-secondary)" }}>{t("DashboardV2.open_your_instagram_profile_to_public_and_try_ag", lang)}</p>
              <Button onClick={() => { const mc = Math.max(giftStatus?.likes_reject_count||0, giftStatus?.followers_reject_count||0); localStorage.setItem(`gift_reject_popup_${mc}`,"1"); setGiftPopupOpen(false); }} className="w-full h-9 text-sm font-semibold rounded-xl" style={{ background:"#ef4444", color:"#fff" }}>{t("DashboardV2.i_understand", lang)}</Button>
            </div>
          </div>
        )}

        {/* ══════════════════════════
            HERO SECTION
        ══════════════════════════ */}
        <div className="relative overflow-hidden" style={{ background:"var(--bg-secondary)", borderBottom:"1px solid var(--border-color)" }}>
          {/* Subtle purple radial glow — dark mode only via CSS variable trick */}
          <div className="absolute inset-0 pointer-events-none" style={{
            background:"radial-gradient(ellipse 70% 80% at 20% 50%, rgba(139,92,246,0.08) 0%, transparent 70%)",
          }} />
          <div className="relative max-w-xl md:max-w-6xl mx-auto px-4 md:px-8 pt-5 pb-5">

            {/* Row: Avatar + Greeting + Settings */}
            <div className="flex items-center gap-3 mb-4">
              {/* Avatar */}
              <div className="relative flex-shrink-0">
                {user?.picture || gravatarUrl
                  ? <img src={user?.picture || gravatarUrl} alt="" className="rounded-2xl object-cover" style={{ width:48, height:48 }} />
                  : <div className="flex items-center justify-center rounded-2xl text-white font-black text-lg" style={{ width:48, height:48, background:"linear-gradient(135deg,#8B5CF6,#4F46E5)" }}>
                      {(firstName[0]||"?").toUpperCase()}
                    </div>
                }
              </div>

              {/* Greeting */}
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-medium" style={{ color:"var(--text-secondary)" }}>
                  {t("DashboardV2.welcome_back", lang)}
                </p>
                <h1 className="text-lg font-black font-outfit leading-tight truncate" style={{ color:"var(--text-primary)" }}>
                  {L(lang,`Hey, ${firstName}! 👋`,`Привет, ${firstName}! 👋`,`!היי, ${firstName} 👋`)}
                </h1>
              </div>

              {/* Settings icon */}
              <button type="button" onClick={() => setActiveTab("settings")}
                className="flex-shrink-0 w-10 h-10 rounded-2xl flex items-center justify-center transition-all press-sm"
                style={{ background: activeTab === "settings" ? "rgba(139,92,246,0.18)" : "var(--surface-2)", border: `1px solid ${activeTab === "settings" ? "rgba(139,92,246,0.4)" : "var(--border-color)"}` }}>
                <Settings className="w-4.5 h-4.5" style={{ color: activeTab === "settings" ? "#8B5CF6" : "var(--text-secondary)" }} />
              </button>
            </div>

            {/* Balance card */}
            <div className="rounded-2xl mb-4 relative overflow-hidden"
              style={{ background:"linear-gradient(140deg, #7C3AED 0%, #4338CA 100%)" }}>
              {/* Dot-grid texture overlay */}
              <div className="absolute inset-0 pointer-events-none"
                style={{ backgroundImage:"radial-gradient(circle, rgba(255,255,255,0.07) 1px, transparent 1px)", backgroundSize:"16px 16px" }} />
              {/* Top-right shimmer orb */}
              <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full pointer-events-none"
                style={{ background:"rgba(255,255,255,0.06)", filter:"blur(18px)" }} />
              <div className="relative px-4 pt-4 pb-3">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <p className="text-[10px] font-semibold tracking-widest uppercase mb-1.5" style={{ color:"rgba(255,255,255,0.5)" }}>
                      {t("dash.ipoints", lang)}
                    </p>
                    <p className="font-black font-outfit leading-none" dir="ltr" style={{ color:"#fff", fontSize:"clamp(2rem,8vw,2.75rem)" }}>
                      {fmtIp(user?.ipoints||0)}
                      <span className="text-base font-bold ms-1.5 align-baseline" style={{ color:"rgba(255,255,255,0.5)" }}>iPoints</span>
                    </p>
                  </div>
                  <div className="flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center mt-0.5"
                    style={{ background:"rgba(255,255,255,0.1)", border:"1px solid rgba(255,255,255,0.12)" }}>
                    <Coins className="w-5 h-5" style={{ color:"rgba(255,255,255,0.8)" }} />
                  </div>
                </div>
                <Link to="/topup" data-testid="tour-topup-card"
                  className="flex items-center justify-center gap-2 h-9 w-full rounded-xl font-bold text-sm press-sm"
                  style={{ background:"rgba(255,255,255,0.13)", color:"rgba(255,255,255,0.92)", border:"1px solid rgba(255,255,255,0.15)" }}>
                  <Wallet className="w-3.5 h-3.5" />
                  {t("DashboardV2.top_up_balance", lang)}
                </Link>
              </div>
            </div>

            {/* Quick actions */}
            <div className="flex gap-2 mb-2">
              <Link to="/shop" data-testid="tour-shop-card"
                className="flex-1 h-10 rounded-xl font-semibold text-sm flex items-center justify-center gap-1.5 press-sm"
                style={{ background:"var(--surface-2)", border:"1px solid var(--border-color)", color:"var(--text-primary)" }}>
                <Heart className="w-4 h-4" style={{ color:"#E1306C" }} />
                {t("Admin.shop", lang)}
              </Link>
              <Link to="/topup"
                className="flex-1 h-10 rounded-xl font-semibold text-sm flex items-center justify-center gap-1.5 press-sm"
                style={{ background:"var(--surface-2)", border:"1px solid var(--border-color)", color:"var(--text-primary)" }}>
                <Wallet className="w-4 h-4" style={{ color:"#F77737" }} />
                {t("nav.topup", lang)}
              </Link>
              <button type="button" onClick={copyRef}
                className="flex-1 h-10 rounded-xl font-semibold text-sm flex items-center justify-center gap-1.5 press-sm relative"
                style={{ background:"var(--surface-2)", border:"1px solid var(--border-color)", color:"var(--text-primary)" }}>
                <Users className="w-4 h-4 flex-shrink-0" style={{ color:"#8B5CF6" }} />
                <span className="flex flex-col items-start leading-none">
                  <span className="text-xs font-semibold">{t("DashboardV2.invite_friend", lang)}</span>
                  <span className="text-[10px] font-medium" style={{ color:"#8B5CF6" }}>+10% iP</span>
                </span>
              </button>
            </div>

          </div>
        </div>

        {/* ══════════════════════════
            TAB PILLS
        ══════════════════════════ */}
        <div className="max-w-xl md:max-w-6xl mx-auto px-4 md:px-8 pt-4 pb-1">
          <div className="flex gap-2 overflow-x-auto" style={{ scrollbarWidth:"none" }}>
            {TABS.map(tab => {
              const isActive = activeTab === tab.id;
              return (
                <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)}
                  className="flex-shrink-0 flex items-center gap-1.5 h-8 rounded-full text-xs font-semibold press-sm"
                  style={{
                    padding: "0 12px",
                    background: isActive ? "#8B5CF6" : "var(--surface-2)",
                    color: isActive ? "#fff" : "var(--text-secondary)",
                    boxShadow: isActive ? "0 4px 14px rgba(139,92,246,0.35)" : "none",
                    transition: "background 200ms cubic-bezier(0.23,1,0.32,1), box-shadow 200ms cubic-bezier(0.23,1,0.32,1), color 200ms cubic-bezier(0.23,1,0.32,1), transform 130ms cubic-bezier(0.23,1,0.32,1)",
                    opacity: isActive ? 1 : 0.85,
                  }}>
                  <span className="flex-shrink-0" style={{ opacity: isActive ? 1 : 0.65 }}>{tab.icon}</span>
                  {tab.label}
                  {tab.count != null && tab.count > 0 && (
                    <span className="text-[10px] font-black w-4 h-4 rounded-full flex items-center justify-center"
                      style={{ background: isActive ? "rgba(255,255,255,0.25)" : "rgba(139,92,246,0.15)", color: isActive ? "#fff" : "#8B5CF6" }}>
                      {tab.count > 9 ? "9+" : tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* ══════════════════════════
            CONTENT
        ══════════════════════════ */}
        <div className="max-w-xl md:max-w-6xl mx-auto px-4 md:px-8 py-4 space-y-4" key={activeTab} style={{ animation:"tabFadeIn 180ms cubic-bezier(0.23,1,0.32,1)" }}>

          {/* ══ OVERVIEW ══ */}
          {activeTab === "overview" && giftStatus && (
            <div className="rounded-2xl border overflow-hidden card-enter card-enter-1" style={{ borderColor:"var(--border-color)", background:"var(--surface-1)" }} data-testid="free-gift-card">
              <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor:"var(--border-color)" }}>
                <Gift className="w-4 h-4" style={{ color:"#F77737" }} />
                <span className="font-bold text-sm" style={{ color:"var(--text-primary)" }}>{t("DashboardV2.free_gifts", lang)}</span>
                {(giftStatus.likes_claimed && giftStatus.followers_claimed) && (
                  <span className="ms-auto text-xs font-bold" style={{ color:"#22c55e" }}>✓ {t("DashboardV2.all_claimed", lang)}</span>
                )}
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold" style={{ color:"var(--text-primary)" }}>❤️ {t("DashboardV2.50_free_likes", lang)}</span>
                    {giftStatus.likes_claimed && <span className="text-xs font-bold" style={{ color:"#22c55e" }}>✓ {t("DashboardV2.claimed", lang)}</span>}
                  </div>
                  {!giftStatus.likes_claimed && (
                    <div className="space-y-2">
                      <div className="relative">
                        <Input value={likesLink} onChange={e => { setLikesLink(e.target.value); if (!likesLinkTouched) setLikesLinkTouched(true); }} onBlur={() => { setLikesLinkTouched(true); const n = canonicalizeInstagramPostUrl(likesLink); if (n) setLikesLink(n); }} placeholder={user?.instagram_username ? `instagram.com/${user.instagram_username}/post` : "instagram.com/p/AbCd123"} className="h-9 text-sm pe-8" style={{ background:"var(--surface-2)", borderColor:"var(--border-color)" }} data-testid="gift-instagram-likes" />
                        {likesLink.trim() && <span className={`absolute end-3 top-1/2 -translate-y-1/2 text-xs ${isValidInstagramPostUrl(likesLink) ? "text-green-400" : "text-red-400"}`}>{isValidInstagramPostUrl(likesLink) ? "✓" : "✗"}</span>}
                      </div>
                      {giftBlockSecs.likes > 0 && <p className="text-xs" style={{ color:"#ef4444" }}>🔒 {L(lang,`Retry in ${fmtBlockTime(giftBlockSecs.likes)}`,`Повтор через ${fmtBlockTime(giftBlockSecs.likes)}`,`נסה בעוד ${fmtBlockTime(giftBlockSecs.likes)}`)}</p>}
                      <Button onClick={() => claimGift("likes", likesLink)} disabled={giftBlockSecs.likes > 0} className="w-full h-9 text-sm font-semibold rounded-xl" style={{ background:"#E1306C", color:"#fff" }} data-testid="claim-likes-btn">
                        {giftBlockSecs.likes > 0 ? `🔒 ${fmtBlockTime(giftBlockSecs.likes)}` : t("DashboardV2.claim_50_likes", lang)}
                      </Button>
                    </div>
                  )}
                </div>
                <div className="h-px" style={{ background:"var(--border-color)" }} />
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold" style={{ color:"var(--text-primary)" }}>👥 {t("DashboardV2.10_free_followers", lang)}</span>
                    {giftStatus.followers_claimed && <span className="text-xs font-bold" style={{ color:"#22c55e" }}>✓ {t("DashboardV2.claimed", lang)}</span>}
                  </div>
                  {!giftStatus.followers_claimed && (
                    <div className="space-y-2">
                      <div className="relative">
                        <Input value={followersLink} onChange={e => { setFollowersLink(e.target.value); if (!followersLinkTouched) setFollowersLinkTouched(true); }} onBlur={() => { setFollowersLinkTouched(true); const n = canonicalizeInstagramProfileUrl(followersLink); if (n) setFollowersLink(n); }} placeholder={user?.instagram_username ? `instagram.com/${user.instagram_username}` : "instagram.com/username"} className="h-9 text-sm pe-8" style={{ background:"var(--surface-2)", borderColor:"var(--border-color)" }} data-testid="gift-instagram-followers" />
                        {followersLink.trim() && <span className={`absolute end-3 top-1/2 -translate-y-1/2 text-xs ${isValidInstagramProfileUrl(followersLink) ? "text-green-400" : "text-red-400"}`}>{isValidInstagramProfileUrl(followersLink) ? "✓" : "✗"}</span>}
                      </div>
                      {giftBlockSecs.followers > 0 && <p className="text-xs" style={{ color:"#833AB4" }}>🔒 {L(lang,`Retry in ${fmtBlockTime(giftBlockSecs.followers)}`,`Повтор через ${fmtBlockTime(giftBlockSecs.followers)}`,`נסה בעוד ${fmtBlockTime(giftBlockSecs.followers)}`)}</p>}
                      <Button onClick={() => claimGift("followers", followersLink)} disabled={giftBlockSecs.followers > 0} className="w-full h-9 text-sm font-semibold rounded-xl" style={{ background:"#833AB4", color:"#fff" }} data-testid="claim-followers-btn">
                        {giftBlockSecs.followers > 0 ? `🔒 ${fmtBlockTime(giftBlockSecs.followers)}` : t("DashboardV2.claim_10_followers", lang)}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ══ OVERVIEW — stats strip ══ */}
          {activeTab === "overview" && (
            <div className="flex items-center gap-0 px-1 card-enter card-enter-1 flex-wrap" style={{ rowGap:"0.5rem" }}>
              {[
                { value: orders.length,               label: t("DashboardV2.orders", lang),   color: "#7aa2ff" },
                { value: activeOrders,                 label: t("DashboardV2.active", lang), color: "#F77737" },
                { value: availTasks.length,            label: t("DashboardV2.tasks", lang),   color: "#22c55e" },
                { value: `${fmtIp(spentIpoints)} iP`,  label: t("DashboardV2.spent_2", lang),  color: "var(--text-secondary)" },
              ].map((s, i) => (
                <div key={i} className="flex items-center gap-2 flex-shrink-0">
                  {i > 0 && <span className="w-px h-4" style={{ background:"var(--border-color)" }} />}
                  <div className="flex items-center gap-2 px-2">
                    <span className="text-base font-black font-outfit" style={{ color: s.color }}>{s.value}</span>
                    <span className="text-xs" style={{ color:"var(--text-secondary)" }}>{s.label}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ══ OVERVIEW — recent activity ══ */}
          {activeTab === "overview" && (
            <div className="space-y-4">
              {/* Recent orders */}
              <div className="rounded-2xl border overflow-hidden card-enter card-enter-2" style={{ borderColor:"var(--border-color)", background:"var(--surface-1)" }}>
                <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor:"var(--border-color)" }}>
                  <span className="font-bold text-sm" style={{ color:"var(--text-primary)" }}>{t("Admin.recent_orders", lang)}</span>
                  <button type="button" onClick={() => setActiveTab("orders")} className="flex items-center gap-0.5 text-xs font-semibold press-sm" style={{ color:"#8B5CF6" }}>
                    {t("DashboardV2.see_all", lang)}<ChevronRight className="w-3.5 h-3.5" style={{ transform: isRTL ? "scaleX(-1)" : "none" }} />
                  </button>
                </div>
                {orders.length === 0 ? (
                  <div className="p-6 text-center">
                    <p className="text-xs mb-3" style={{ color:"var(--text-secondary)" }}>{t("Admin.no_orders_yet", lang)}</p>
                    <Link to="/shop" className="inline-flex items-center gap-1.5 h-8 px-4 rounded-xl text-xs font-semibold" style={{ background:"#8B5CF6", color:"#fff" }}>
                      <Package className="w-3.5 h-3.5" />{t("DashboardV2.go_to_shop", lang)}
                    </Link>
                  </div>
                ) : (
                  <div className="divide-y" style={{ borderColor:"var(--border-color)" }}>
                    {orders.slice(0,5).map(order => {
                      const s = order.public_status||"sent"; const tone = orderStatusTone(s);
                      return (
                        <button key={order.order_id} type="button" onClick={() => setSelectedOrder(order)}
                          className="w-full flex items-center gap-3 px-4 py-3 text-start press-row">
                          <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background:`${tone.color}14` }}>
                            <Package className="w-4 h-4" style={{ color:tone.color }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold truncate" style={{ color:"var(--text-primary)" }}>#{order.order_number} · {orderItemsSummary(lang, order.items)}</div>
                            <div className="text-[11px]" style={{ color:"var(--text-secondary)" }}>{formatDT(order.created_at)}</div>
                          </div>
                          <span className="text-xs font-semibold flex-shrink-0 px-2 py-0.5 rounded-full" style={{ background:`${tone.color}14`, color:tone.color }}>{orderStatusLabel(lang, s)}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              {/* iPoints history preview */}
              {ipHistory.length > 0 && (
                <div className="rounded-2xl border overflow-hidden card-enter card-enter-3" style={{ borderColor:"var(--border-color)", background:"var(--surface-1)" }}>
                  <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor:"var(--border-color)" }}>
                    <span className="font-bold text-sm" style={{ color:"var(--text-primary)" }}>{t("Admin.ipoints_history", lang)}</span>
                    <button type="button" onClick={() => setActiveTab("rewards")} className="flex items-center gap-0.5 text-xs font-semibold press-sm" style={{ color:"#8B5CF6" }}>
                      {t("DashboardV2.see_all", lang)}<ChevronRight className="w-3.5 h-3.5" style={{ transform: isRTL ? "scaleX(-1)" : "none" }} />
                    </button>
                  </div>
                  <div className="divide-y" style={{ borderColor:"var(--border-color)" }}>
                    {ipHistory.slice(0,5).map((entry) => {
                      const cfg = IP_SOURCE_CFG[entry.source_type] || IP_SOURCE_CFG.admin;
                      const Icon = cfg.icon; const isGain = entry.delta > 0;
                      const label = entry.label?.[lang] || entry.label?.en || entry.source_type;
                      return (
                        <div key={entry.ledger_id || `${entry.source_type}-${entry.created_at}`} className="flex items-center gap-3 px-4 py-3" style={{ borderColor:"var(--border-color)" }}>
                          <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background:`${cfg.color}14` }}>
                            <Icon className="w-4 h-4" style={{ color:cfg.color }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold truncate" style={{ color:"var(--text-primary)" }}>{label}</div>
                            <div className="text-[11px]" style={{ color:"var(--text-secondary)" }}>{formatDT(entry.created_at).split(",")[0]}</div>
                          </div>
                          <span className="text-sm font-black font-outfit flex-shrink-0" style={{ color: isGain ? "#22c55e" : "#ef4444" }}>{isGain ? "+" : "−"}{fmtIp(Math.abs(entry.delta))}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ══ ORDERS ══ */}
          {activeTab === "orders" && (
            <div className="space-y-3">
              {/* Stats row */}
              <div className="flex items-center gap-0 px-1 flex-wrap" style={{ rowGap:"0.5rem" }}>
                {[
                  { value:orders.length,              label:t("DashboardV2.total", lang),    color:"var(--text-primary)" },
                  { value:activeOrders,                label:t("DashboardV2.active", lang),  color:"#F77737" },
                  { value:completedOrders,             label:t("Admin.done_2", lang),   color:"#22c55e" },
                  { value:`${fmtIp(spentIpoints)} iP`, label:t("DashboardV2.spent_2", lang), color:"#F77737" },
                ].map((s, i) => (
                  <div key={i} className="flex items-center gap-2 flex-shrink-0">
                    {i > 0 && <span className="w-px h-4 flex-shrink-0" style={{ background:"var(--border-color)" }} />}
                    <div className="flex items-center gap-1.5 px-2">
                      <span className="text-base font-black font-outfit" style={{ color:s.color }}>{s.value}</span>
                      <span className="text-xs" style={{ color:"var(--text-secondary)" }}>{s.label}</span>
                    </div>
                  </div>
                ))}
              </div>

              {orders.length === 0 ? (
                <EmptyState icon={<Package className="w-6 h-6" />} title={t("Admin.no_orders_yet", lang)} description={t("DashboardV2.your_first_order_will_appear_here", lang)} actionTo="/shop" actionLabel={t("DashboardV2.go_to_shop", lang)} />
              ) : (
                <>
                  <div className="rounded-2xl border overflow-hidden" style={{ borderColor:"var(--border-color)", background:"var(--surface-1)" }}>
                    <div className="divide-y" style={{ borderColor:"var(--border-color)" }}>
                      {pagedOrders.map(order => {
                        const s = order.public_status||"sent"; const tone = orderStatusTone(s);
                        return (
                          <button key={order.order_id} type="button" onClick={() => setSelectedOrder(order)}
                            className="w-full flex items-center gap-3 px-4 py-3.5 text-start press-row">
                            <div className="w-2 h-2 rounded-full flex-shrink-0 mt-0.5" style={{ background:tone.color }} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className="font-bold text-sm" style={{ color:"var(--text-primary)" }}>#{order.order_number}</span>
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background:`${tone.color}14`, color:tone.color }}>
                                  {orderStatusLabel(lang, s)}
                                </span>
                                {order?.provider_sync_status === "partial" && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background:"rgba(168,85,247,0.1)", color:"#a855f7" }}>{t("Admin.partial", lang)}</span>
                                )}
                              </div>
                              <div className="text-xs truncate" style={{ color:"var(--text-secondary)" }}>{orderItemsSummary(lang, order.items)}</div>
                              <div className="text-[11px] mt-0.5" style={{ color:"var(--text-secondary)" }}>{formatDT(order.created_at)} · {order.total_ipoints > 0 ? `${order.total_ipoints} iP` : t("Admin.free", lang)}</div>
                            </div>
                            <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" style={{ color:"var(--text-secondary)", transform: isRTL ? "scaleX(-1)" : "none" }} />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {orders.length > 6 && (
                    <div className="flex items-center justify-center gap-3 pt-1">
                      <button type="button" disabled={ordersPage===0} onClick={() => setOrdersPage(p=>p-1)} className="w-8 h-8 rounded-xl flex items-center justify-center disabled:opacity-30 press-sm" style={{ background:"var(--surface-1)", border:"1px solid var(--border-color)" }}>
                        <ChevronLeft className="w-4 h-4" style={{ transform: isRTL ? "scaleX(-1)" : "none" }} />
                      </button>
                      <span className="text-xs" style={{ color:"var(--text-secondary)" }}>{ordersPage+1} / {Math.ceil(orders.length/6)}</span>
                      <button type="button" disabled={(ordersPage+1)*6>=orders.length} onClick={() => setOrdersPage(p=>p+1)} className="w-8 h-8 rounded-xl flex items-center justify-center disabled:opacity-30 press-sm" style={{ background:"var(--surface-1)", border:"1px solid var(--border-color)" }}>
                        <ChevronRight className="w-4 h-4" style={{ transform: isRTL ? "scaleX(-1)" : "none" }} />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ══ TOPUPS ══ */}
          {activeTab === "topups" && (
            <div className="space-y-3">
              {/* Big CTA */}
              <Link to="/topup"
                className="flex items-center justify-center gap-2 h-12 w-full rounded-2xl font-bold text-sm press-btn"
                style={{ background:"linear-gradient(135deg, #8B5CF6, #4F46E5)", color:"#fff" }}>
                <Wallet className="w-4 h-4" />
                {t("DashboardV2.top_up_balance_2", lang)}
              </Link>

              {/* Stats row */}
              <div className="flex items-center gap-0 px-1 flex-wrap" style={{ rowGap:"0.5rem" }}>
                {[
                  { value:topups.length,                                     label:t("DashboardV2.total", lang),       color:"var(--text-primary)" },
                  { value:approvedTopups.length,                              label:t("Admin.approved", lang), color:"#22c55e" },
                  { value:`${fmtIp(totalApprovedIp)} iP`,                    label:t("DashboardV2.credited", lang), color:"#F77737" },
                  { value:`${Number(totalApprovedIls||0).toLocaleString()} ₪`, label:t("DashboardV2.all_time", lang),  color:"var(--text-primary)" },
                ].map((s, i) => (
                  <div key={i} className="flex items-center gap-2 flex-shrink-0">
                    {i > 0 && <span className="w-px h-4 flex-shrink-0" style={{ background:"var(--border-color)" }} />}
                    <div className="flex items-center gap-1.5 px-2">
                      <span className="text-base font-black font-outfit" style={{ color:s.color }}>{s.value}</span>
                      <span className="text-xs" style={{ color:"var(--text-secondary)" }}>{s.label}</span>
                    </div>
                  </div>
                ))}
              </div>

              {topups.length === 0 ? (
                <EmptyState icon={<Wallet className="w-6 h-6" />} title={t("Admin.no_top_ups_yet", lang)} description={t("DashboardV2.create_your_first_balance_top_up", lang)} actionTo="/topup" actionLabel={t("DashboardV2.top_up_2", lang)} />
              ) : (
                <>
                  <div className="rounded-2xl border overflow-hidden" style={{ borderColor:"var(--border-color)", background:"var(--surface-1)" }}>
                    <div className="divide-y" style={{ borderColor:"var(--border-color)" }}>
                      {pagedTopups.map(topup => {
                        const tone = topupStatusTone(topup.status); const bonus = getTopupBonusTotal(topup);
                        return (
                          <button key={topup.topup_id} type="button" onClick={() => setSelectedTopup(topup)}
                            className="w-full flex items-center gap-3 px-4 py-3.5 text-start press-row">
                            {/* Icon */}
                            <div className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background:`${tone.color}18` }}>
                              <ArrowUpRight className="w-4 h-4" style={{ color:tone.color }} />
                            </div>
                            {/* Main info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-baseline justify-between gap-2">
                                <span className="font-bold text-[15px] font-outfit" dir="ltr" style={{ color:"var(--text-primary)" }}>₪{Number(topup.amount_ils||0).toLocaleString()}</span>
                                <span className="font-bold text-sm font-outfit flex-shrink-0" dir="ltr" style={{ color:"#F77737" }}>+{fmtIp(topup.ipoints_amount||0)} iP</span>
                              </div>
                              <div className="flex items-center justify-between gap-2 mt-0.5">
                                <div className="flex items-center gap-1.5 min-w-0">
                                  <span className="text-[11px]" dir="ltr" style={{ color:"var(--text-secondary)" }}>{formatDT(topup.created_at).split(",")[0]}</span>
                                  <span className="text-[10px] w-0.5 h-0.5 rounded-full flex-shrink-0" style={{ background:"var(--text-secondary)", opacity:0.4 }} />
                                  <span className="text-[10px] font-semibold flex-shrink-0" style={{ color:tone.color }}>{topupStatusLabel(lang, topup.status)}</span>
                                </div>
                                {bonus > 0 && (
                                  <span className="text-[10px] font-bold flex-shrink-0" dir="ltr" style={{ color:"#22c55e" }}>+{Number(bonus).toLocaleString()} {t("DashboardV2.bonus", lang)}</span>
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {topups.length > 6 && (
                    <div className="flex items-center justify-center gap-3 pt-1">
                      <button type="button" disabled={topupsPage===0} onClick={() => setTopupsPage(p=>p-1)} className="w-8 h-8 rounded-xl flex items-center justify-center disabled:opacity-30 press-sm" style={{ background:"var(--surface-1)", border:"1px solid var(--border-color)" }}>
                        <ChevronLeft className="w-4 h-4" style={{ transform: isRTL ? "scaleX(-1)" : "none" }} />
                      </button>
                      <span className="text-xs" style={{ color:"var(--text-secondary)" }}>{topupsPage+1} / {Math.ceil(topups.length/6)}</span>
                      <button type="button" disabled={(topupsPage+1)*6>=topups.length} onClick={() => setTopupsPage(p=>p+1)} className="w-8 h-8 rounded-xl flex items-center justify-center disabled:opacity-30 press-sm" style={{ background:"var(--surface-1)", border:"1px solid var(--border-color)" }}>
                        <ChevronRight className="w-4 h-4" style={{ transform: isRTL ? "scaleX(-1)" : "none" }} />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ══ TASKS ══ */}
          {activeTab === "tasks" && (
            <div className="space-y-4">

              {/* ── Earnings potential banner ── */}
              {availTasks.length > 0 && (
                <div className="rounded-2xl px-3 py-2.5 flex items-center gap-2.5 min-w-0"
                  style={{ background:"rgba(139,92,246,0.07)", border:"1px solid rgba(139,92,246,0.18)", borderLeft:"3px solid #8B5CF6" }}>
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background:"rgba(247,119,55,0.12)" }}>
                    <Coins className="w-4 h-4" style={{ color:"#F77737" }} />
                  </div>
                  <p className="flex-1 min-w-0 text-xs sm:text-sm truncate" style={{ color:"var(--text-primary)" }}>
                    <span className="font-bold" style={{ color:"#8B5CF6" }}>{availTasks.length}</span>{" "}
                    <span style={{ color:"var(--text-secondary)" }}>
                      {t("DashboardV2.tasks_available", lang)}
                    </span>
                  </p>
                  <span className="flex-shrink-0 text-sm font-black font-outfit whitespace-nowrap" style={{ color:"#F77737" }}>
                    +{totalAvailableIp.toLocaleString()}<span className="text-[11px] font-bold ms-0.5">iP</span>
                  </span>
                </div>
              )}

              {/* ── Stats row ── */}
              {tasks.length > 0 && (
                <div className="flex items-center gap-0 px-1 flex-wrap" style={{ rowGap:"0.5rem" }}>
                  {[
                    { value:availTasks.length,   label:t("DashboardV2.available", lang),   color:"#8B5CF6" },
                    { value:pendingTasks.length,  label:t("DashboardV2.in_review", lang),  color:"#F77737" },
                    { value:doneTasks.length,     label:t("DashboardV2.completed", lang),  color:"#22c55e" },
                  ].map((s, i) => (
                    <div key={i} className="flex items-center gap-2 flex-shrink-0">
                      {i > 0 && <span className="w-px h-4 flex-shrink-0" style={{ background:"var(--border-color)" }} />}
                      <div className="flex items-center gap-1.5 px-2">
                        <span className="text-base font-black font-outfit" style={{ color:s.color }}>{s.value}</span>
                        <span className="text-xs" style={{ color:"var(--text-secondary)" }}>{s.label}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ── Active task cards ── */}
              {activeTasks.length === 0 && doneTasks.length === 0 ? (
                <EmptyState icon={<ListChecks className="w-6 h-6" />}
                  title={t("DashboardV2.no_tasks_available", lang)}
                  description={t("DashboardV2.check_back_later", lang)} />
              ) : activeTasks.length === 0 ? (
                <div className="rounded-2xl border p-6 text-center" style={{ borderColor:"var(--border-color)", background:"var(--surface-1)" }}>
                  <CheckCircle className="w-8 h-8 mx-auto mb-2" style={{ color:"#22c55e" }} />
                  <p className="font-bold text-sm mb-0.5" style={{ color:"var(--text-primary)" }}>
                    {t("DashboardV2.all_done", lang)}
                  </p>
                  <p className="text-xs" style={{ color:"var(--text-secondary)" }}>
                    {t("DashboardV2.you_ve_completed_all_available_tasks", lang)}
                  </p>
                </div>
              ) : (
                <div className="grid gap-3 md:grid-cols-2">
                  {activeTasks.map((task, idx) => {
                    const meta = TASK_META[task.task_type] || TASK_META.custom;
                    const sub = task.my_submissions?.[0]; const myStatus = sub?.status;
                    const timeLeft = getTimeLeft(task.expires_at);
                    const total = task.max_completions || 0; const done = task.completions_count || 0;
                    const spotsLeft = total > 0 ? total - done : null;
                    const fillPct = total > 0 ? Math.min(100, Math.round((done/total)*100)) : 0;
                    return (
                      <div key={task.task_id} className="rounded-2xl overflow-hidden flex flex-col card-enter"
                        style={{ background:"var(--surface-1)", border:"1px solid var(--border-color)", borderLeft:`3px solid ${meta.color}`, animationDelay:`${idx*55}ms` }}
                        data-testid={`task-${task.task_id}`}>
                        <div className="p-4 flex flex-col flex-1">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <TaskPlatformIcon type={task.task_type} color={meta.color} size={14} />
                              <span className="text-xs font-semibold" style={{ color:meta.color }}>{meta.label[lang]||meta.label.en}</span>
                            </div>
                            <span className="text-xs font-bold font-outfit flex items-center gap-0.5 reward-pop" style={{ color:"#F77737" }}>
                              <Coins className="w-3 h-3" />+{task.reward_ipoints}
                            </span>
                          </div>
                          <h3 className="font-bold text-sm mb-1" style={{ color:"var(--text-primary)" }}>{task.title?.[lang]||task.title?.en}</h3>
                          <p className="text-xs leading-relaxed flex-1 mb-3" style={{ color:"var(--text-secondary)" }}>{task.description?.[lang]||task.description?.en}</p>
                          {task.link_url && (
                            <a href={task.link_url} target="_blank" rel="noopener noreferrer"
                              className="flex items-center gap-1.5 mb-3 text-xs font-semibold press-sm"
                              style={{ color:meta.color }} data-testid={`task-link-${task.task_id}`}>
                              <ArrowUpRight className="w-3.5 h-3.5 flex-shrink-0" />
                              <span className="truncate">{task.link_label?.[lang]||task.link_label?.en||task.link_url}</span>
                            </a>
                          )}
                          {(timeLeft || spotsLeft !== null || total === 0) && (
                            <div className="flex items-center gap-3 mb-3 flex-wrap">
                              {timeLeft && <span className="flex items-center gap-1 text-[11px]" style={{ color:"var(--text-secondary)" }}><Timer className="w-3 h-3" />{timeLeft}</span>}
                              {spotsLeft !== null && <span className="flex items-center gap-1 text-[11px]" style={{ color:"var(--text-secondary)" }}><Users className="w-3 h-3" />{spotsLeft} {t("DashboardV2.spots", lang)}</span>}
                              {total === 0 && <span className="flex items-center gap-1 text-[11px]" style={{ color:"var(--text-secondary)" }}><Users className="w-3 h-3" />{t("DashboardV2.unlimited", lang)}</span>}
                            </div>
                          )}
                          {total > 0 && (
                            <div className="mb-3">
                              <div className="h-1 rounded-full overflow-hidden" style={{ background:"var(--surface-2)" }}>
                                <div className="h-full rounded-full spot-bar-fill" style={{ width:`${fillPct}%`, background:meta.color, opacity:0.7 }} />
                              </div>
                              <div className="flex justify-between mt-1">
                                <span className="text-[10px]" style={{ color:"var(--text-secondary)" }}>{done}/{total}</span>
                                <span className="text-[10px]" style={{ color:"var(--text-secondary)" }}>{fillPct}%</span>
                              </div>
                            </div>
                          )}
                          {myStatus === "pending" ? (
                            <div className="flex items-center gap-2 py-2 text-xs font-semibold status-pending" style={{ color:"#F77737" }}>
                              <Clock className="w-3.5 h-3.5" />{t("DashboardV2.under_review", lang)}
                            </div>
                          ) : myStatus === "rejected" ? (
                            <div className="space-y-2">
                              <div className="flex items-start gap-2 py-2 text-xs font-semibold" style={{ color:"#ef4444" }}>
                                <span className="flex-shrink-0 mt-0.5">✗</span>
                                <span>{t("order.rejected", lang)}{sub?.admin_note && ` — ${sub.admin_note}`}</span>
                              </div>
                              <label className="w-full h-9 rounded-xl font-semibold text-xs flex items-center justify-center gap-2 cursor-pointer press-btn" style={{ background:"#8B5CF6", color:"#fff" }}>
                                <Upload className="w-3.5 h-3.5" />{t("DashboardV2.try_again", lang)}
                                <input type="file" className="hidden" accept="image/*" onChange={e => e.target.files[0] && uploadProof(task.task_id, e.target.files[0])} disabled={uploading===task.task_id} />
                              </label>
                            </div>
                          ) : (
                            <label className="w-full h-10 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 cursor-pointer press-btn"
                              style={{ background: uploading===task.task_id ? "rgba(139,92,246,0.4)" : "#8B5CF6", color:"#fff" }}
                              data-testid={`submit-task-${task.task_id}`}>
                              {uploading===task.task_id
                                ? (<><div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />{t("DashboardV2.uploading", lang)}</>)
                                : (<><Upload className="w-4 h-4" />{t("DashboardV2.upload_proof", lang)}</>)}
                              <input type="file" className="hidden" accept="image/*" onChange={e => e.target.files[0] && uploadProof(task.task_id, e.target.files[0])} disabled={uploading===task.task_id} />
                            </label>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* ── Completed history toggle ── */}
              {doneTasks.length > 0 && (
                <div>
                  {/* Toggle button */}
                  <button
                    type="button"
                    onClick={() => setShowCompletedHistory(p => !p)}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-2xl press-sm"
                    style={{ background:"var(--surface-1)", border:"1px solid var(--border-color)" }}>
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background:"rgba(34,197,94,0.12)" }}>
                        <CheckCircle className="w-3.5 h-3.5" style={{ color:"#22c55e" }} />
                      </div>
                      <span className="font-semibold text-sm" style={{ color:"var(--text-primary)" }}>
                        {t("DashboardV2.completed_tasks", lang)}
                      </span>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                        style={{ background:"rgba(34,197,94,0.12)", color:"#22c55e" }}>
                        {doneTasks.length}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs font-bold font-outfit" style={{ color:"#22c55e" }}>
                        +{totalEarnedIp.toLocaleString()} iP
                      </span>
                      <ChevronRight className="w-4 h-4 flex-shrink-0"
                        style={{ color:"var(--text-secondary)", transform: showCompletedHistory ? "rotate(90deg)" : (isRTL ? "scaleX(-1)" : "none"), transition:"transform 220ms cubic-bezier(0.23,1,0.32,1)" }} />
                    </div>
                  </button>

                  {/* History list — flat, no cards per design */}
                  {showCompletedHistory && (
                    <div className="mt-2 rounded-2xl overflow-hidden" style={{ border:"1px solid var(--border-color)" }}>
                      {doneTasks.map((task, idx) => {
                        const meta = TASK_META[task.task_type] || TASK_META.custom;
                        return (
                          <div key={task.task_id}
                            className="flex items-center gap-3 px-4 py-3"
                            style={{ borderBottom: idx < doneTasks.length - 1 ? "1px solid var(--border-color)" : "none", background:"var(--surface-1)" }}>
                            <div className="flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center"
                              style={{ background:`${meta.color}14` }}>
                              <TaskPlatformIcon type={task.task_type} color={meta.color} size={14} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold truncate" style={{ color:"var(--text-primary)" }}>
                                {task.title?.[lang] || task.title?.en}
                              </div>
                              <div className="text-xs mt-0.5" style={{ color:meta.color }}>
                                {meta.label[lang] || meta.label.en}
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <CheckCircle className="w-3.5 h-3.5" style={{ color:"#22c55e" }} />
                              <span className="text-sm font-black font-outfit" style={{ color:"#22c55e" }}>
                                +{Number(task.reward_ipoints||0).toLocaleString()} iP
                              </span>
                            </div>
                          </div>
                        );
                      })}
                      {/* Total earned row */}
                      <div className="flex items-center justify-between px-4 py-3"
                        style={{ borderTop:"1px solid var(--border-color)", background:"var(--surface-2)" }}>
                        <span className="text-xs font-semibold" style={{ color:"var(--text-secondary)" }}>
                          {t("DashboardV2.total_earned", lang)}
                        </span>
                        <span className="text-base font-black font-outfit" style={{ color:"#22c55e" }}>
                          +{totalEarnedIp.toLocaleString()} iP
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

            </div>
          )}

          {/* ══ REWARDS & SETTINGS ══ */}
          {activeTab === "rewards" && (
            <div className="space-y-4">

              {/* Referral — most prominent */}
              <div className="rounded-2xl border overflow-hidden card-enter card-enter-1" style={{ borderColor:"var(--border-color)", background:"var(--surface-1)" }} data-testid="referral-card">
                <div className="px-4 py-3 border-b flex items-center gap-2.5" style={{ borderColor:"var(--border-color)" }}>
                  <div className="w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background:"rgba(131,58,180,0.12)" }}>
                    <Share2 className="w-4 h-4" style={{ color:"#833AB4" }} />
                  </div>
                  <span className="font-bold text-sm flex-1" style={{ color:"var(--text-primary)" }}>{t("DashboardV2.referral_program", lang)}</span>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                    style={{ background:"rgba(131,58,180,0.1)", color:"#833AB4" }}>
                    10% {t("DashboardV2.from_friends", lang)}
                  </span>
                </div>
                <div className="p-4 space-y-4">
                  {/* Stats */}
                  <div className="flex items-center gap-5">
                    <div className="flex-1 rounded-xl p-3 text-center" style={{ background:"var(--surface-2)" }}>
                      <div className="text-xl font-black font-outfit" style={{ color:"#833AB4" }}>{referralStats?.referral_count||0}</div>
                      <div className="text-[10px] mt-0.5" style={{ color:"var(--text-secondary)" }}>{t("DashboardV2.friends_joined", lang)}</div>
                    </div>
                    <div className="flex-1 rounded-xl p-3 text-center" style={{ background:"var(--surface-2)" }}>
                      <div className="text-xl font-black font-outfit" style={{ color:"#7aa2ff" }}>{fmtIp(referralStats?.total_earned||0)}</div>
                      <div className="text-[10px] mt-0.5" style={{ color:"var(--text-secondary)" }}>{t("DashboardV2.ip_earned", lang)}</div>
                    </div>
                  </div>

                  {/* Recent referral earnings */}
                  {referralStats?.recent_earnings?.length > 0 && (
                    <div className="rounded-xl border divide-y" style={{ borderColor:"var(--border-color)" }}>
                      <div className="px-3 py-2 text-[10px] uppercase tracking-wider font-semibold" style={{ color:"var(--text-secondary)" }}>
                        {t("DashboardV2.recent_earnings", lang)}
                      </div>
                      {referralStats.recent_earnings.map((e, i) => {
                        const lbl = typeof e.label === "object" ? (e.label?.[lang] || e.label?.en || "") : (e.label || "");
                        return (
                          <div key={i} className="flex items-center gap-2 px-3 py-2">
                            <span className="flex-1 text-xs truncate" style={{ color:"var(--text-secondary)" }}>{lbl}</span>
                            <span className="text-xs font-bold font-outfit flex-shrink-0" style={{ color:"#833AB4" }}>+{fmtIp(e.delta)}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Referral link */}
                  {referralLink && (
                    <div className="rounded-xl border p-3" style={{ borderColor:"rgba(131,58,180,0.25)", background:"rgba(131,58,180,0.06)" }}>
                      <div className="text-[10px] uppercase tracking-wider font-semibold mb-2" style={{ color:"#833AB4" }}>
                        {t("DashboardV2.your_referral_link", lang)}
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 text-xs truncate font-medium" style={{ color:"var(--text-primary)" }}>{referralLink}</div>
                        <button type="button" onClick={copyRef}
                          className="flex-shrink-0 h-8 px-3 rounded-xl font-semibold text-xs flex items-center gap-1.5 press-sm"
                          style={{ background:"#833AB4", color:"#fff" }}>
                          <Copy className="w-3 h-3" />
                          {t("DashboardV2.copy", lang)}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* How it works */}
                  <div>
                    <div className="text-xs font-semibold mb-2" style={{ color:"var(--text-secondary)" }}>
                      {t("DashboardV2.how_it_works", lang)}
                    </div>
                    <div className="space-y-2">
                      {[
                        t("DashboardV2.share_your_link_with_friends", lang),
                        t("DashboardV2.friend_registers_and_tops_up", lang),
                        t("DashboardV2.you_earn_10_in_ipoints", lang),
                      ].map((step, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-black"
                            style={{ background:"rgba(131,58,180,0.12)", color:"#833AB4" }}>{i+1}</div>
                          <span className="text-xs" style={{ color:"var(--text-secondary)" }}>{step}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Referral code input */}
                  {!user?.referred_by && (
                    <div>
                      {showRefInput ? (
                        <div className="flex gap-2">
                          <Input value={refInput} onChange={e => setRefInput(e.target.value)} placeholder={t("DashboardV2.referral_code", lang)} className="flex-1 h-9 text-sm" data-testid="ref-input" />
                          <Button size="sm" onClick={applyReferral} className="h-9 px-3 text-sm font-semibold rounded-xl" style={{ background:"#833AB4", color:"#fff" }}>{t("dash.apply", lang)}</Button>
                        </div>
                      ) : (
                        <button type="button" onClick={() => setShowRefInput(true)} className="text-xs font-semibold press-sm" style={{ color:"var(--text-secondary)" }} data-testid="enter-ref-code">
                          {t("DashboardV2.have_a_referral_code_enter_it_here", lang)}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* My Referrer card */}
              {user?.referred_by && myReferrer?.referrer && (
                <div className="rounded-2xl border overflow-hidden" style={{ borderColor:"var(--border-color)", background:"var(--surface-1)" }}>
                  <div className="px-4 py-3 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background:"rgba(139,92,246,0.12)" }}>
                      <UserCheck className="w-4 h-4" style={{ color:"#8B5CF6" }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-wider font-semibold mb-0.5" style={{ color:"var(--text-secondary)" }}>
                        {t("DashboardV2.referred_by", lang)}
                      </div>
                      <div className="text-sm font-bold" style={{ color:"var(--text-primary)" }}>{myReferrer.referrer.name}</div>
                    </div>
                    {myReferrer.referrer.earned_for_them > 0 && (
                      <div className="text-right flex-shrink-0">
                        <div className="text-sm font-black font-outfit" style={{ color:"#8B5CF6" }}>+{fmtIp(myReferrer.referrer.earned_for_them)}</div>
                        <div className="text-[10px]" style={{ color:"var(--text-secondary)" }}>iP {t("DashboardV2.they_earned", lang)}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Promo code */}
              <div className="rounded-2xl border overflow-hidden card-enter card-enter-3" style={{ borderColor:"var(--border-color)", background:"var(--surface-1)" }} data-testid="promo-card">
                <div className="px-4 py-3 border-b flex items-center gap-2.5" style={{ borderColor:"var(--border-color)" }}>
                  <div className="w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background:"rgba(34,197,94,0.12)" }}>
                    <Tag className="w-4 h-4" style={{ color:"#22c55e" }} />
                  </div>
                  <span className="font-bold text-sm" style={{ color:"var(--text-primary)" }}>{t("DashboardV2.promo_code_3", lang)}</span>
                </div>
                <div className="p-4 space-y-3">
                  <div className="flex gap-2">
                    <Input value={promoInput} onChange={e => setPromoInput(e.target.value.toUpperCase())} placeholder={t("DashboardV2.enter_code", lang)} className="flex-1 h-10 text-sm uppercase font-outfit rounded-xl" onKeyDown={e => { if (e.key==="Enter") applyPromo(); }} data-testid="dashboard-promo-input"
                      style={{ background:"var(--surface-2)", borderColor:"var(--border-color)", color:"var(--text-primary)" }} />
                    <Button size="sm" onClick={applyPromo} className="h-10 px-4 text-sm font-semibold rounded-xl" style={{ background:"#22c55e", color:"#fff" }} data-testid="dashboard-promo-apply">{t("dash.apply", lang)}</Button>
                  </div>
                  {promoResult && (
                    <div className="rounded-xl p-3 text-xs leading-5" style={{ background: promoResult.tone==="success" ? "rgba(34,197,94,0.08)" : "rgba(64,93,230,0.08)", color: promoResult.tone==="success" ? "#22c55e" : "#7aa2ff", border:`1px solid ${promoResult.tone==="success" ? "rgba(34,197,94,0.15)" : "rgba(64,93,230,0.15)"}` }}>
                      {promoResult.text}
                      {promoResult.tone==="info" && <div className="mt-1"><Link to="/topup" className="font-bold underline">{t("DashboardV2.go_to_top_up", lang)}</Link></div>}
                    </div>
                  )}
                </div>
              </div>

              {/* iPoints History */}
              <div className="card-enter card-enter-4"><IPointsHistory ipHistory={ipHistory} lang={lang} /></div>
            </div>
          )}

          {/* ══ SETTINGS ══ */}
          {activeTab === "settings" && (
            <div className="space-y-4">

              {/* Profile */}
              {(() => {
                const isGoogle = user?.auth_provider === "google";
                const hasPassword = user?.has_password;

                const savePassword = async () => {
                  if (pwForm.next.length < 8) { toast.error(L(lang,"Min 8 characters","Минимум 8 символов","לפחות 8 תווים")); return; }
                  if (pwForm.next !== pwForm.confirm) { toast.error(L(lang,"Passwords don't match","Пароли не совпадают","הסיסמאות לא תואמות")); return; }
                  setPwSaving(true);
                  try {
                    const r = await fetch(`${API}/auth/set-password`, {
                      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ current_password: hasPassword ? pwForm.current : undefined, new_password: pwForm.next }),
                    });
                    const d = await r.json().catch(() => ({}));
                    if (!r.ok) throw new Error(d.detail || "Error");
                    toast.success(L(lang,"Password saved","Пароль сохранён","הסיסמה נשמרה"));
                    setPwForm({ current: "", next: "", confirm: "" });
                    setPwOpen(false);
                  } catch (e) { toast.error(e.message); }
                  setPwSaving(false);
                };

                return (
                  <div className="rounded-2xl border card-enter card-enter-1 overflow-hidden" style={{ borderColor:"var(--border-color)", background:"var(--surface-1)" }}>
                    {/* Header */}
                    <div className="px-4 py-3 border-b flex items-center gap-2.5" style={{ borderColor:"var(--border-color)" }}>
                      <div className="w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background:"rgba(139,92,246,0.12)" }}>
                        <User className="w-4 h-4" style={{ color:"#8B5CF6" }} />
                      </div>
                      <span className="font-bold text-sm" style={{ color:"var(--text-primary)" }}>
                        {L(lang,"Profile","Профиль","פרופיל")}
                      </span>
                      {isGoogle && (
                        <span className="ms-auto text-[10px] px-2 py-0.5 rounded-full font-semibold flex items-center gap-1"
                          style={{ background:"rgba(66,133,244,0.12)", color:"#4285F4", border:"1px solid rgba(66,133,244,0.2)" }}>
                          <svg className="w-3 h-3" viewBox="0 0 24 24"><path d="M23.52 12.27c0-.79-.07-1.55-.2-2.27H12v4.31h6.48a5.54 5.54 0 0 1-2.4 3.64v3.02h3.88c2.27-2.09 3.56-5.17 3.56-8.7Z" fill="#4285F4"/><path d="M12 24c3.24 0 5.95-1.07 7.93-2.91l-3.88-3.02c-1.07.72-2.44 1.15-4.05 1.15-3.11 0-5.74-2.1-6.68-4.92H1.3v3.11A12 12 0 0 0 12 24Z" fill="#34A853"/><path d="M5.32 14.3A7.21 7.21 0 0 1 4.95 12c0-.8.14-1.57.37-2.3V6.59H1.3A12 12 0 0 0 0 12c0 1.94.46 3.77 1.3 5.41l4.02-3.11Z" fill="#FBBC05"/><path d="M12 4.77c1.76 0 3.35.61 4.6 1.82l3.45-3.45C17.94 1.15 15.23 0 12 0A12 12 0 0 0 1.3 6.59L5.32 9.7C6.26 6.87 8.89 4.77 12 4.77Z" fill="#EA4335"/></svg>
                          Google
                        </span>
                      )}
                    </div>

                    {/* Photo section */}
                    <div className="px-4 py-4 border-b" style={{ borderColor:"var(--border-color)" }}>
                      <div className="flex items-center gap-4">
                        <div className="relative flex-shrink-0">
                          <div className="w-16 h-16 rounded-2xl overflow-hidden" style={{ border:"2px solid var(--border-color)" }}>
                            {user?.picture || gravatarUrl
                              ? <img src={user?.picture || gravatarUrl} alt="" className="w-full h-full object-cover" />
                              : <div className="w-full h-full flex items-center justify-center font-black text-xl" style={{ background:"linear-gradient(135deg,#8B5CF6,#4F46E5)", color:"#fff" }}>
                                  {(firstName[0]||"?").toUpperCase()}
                                </div>
                            }
                          </div>
                          {pictureSaving && (
                            <div className="absolute inset-0 rounded-2xl flex items-center justify-center" style={{ background:"rgba(0,0,0,0.55)" }}>
                              <div className="w-4 h-4 border-2 rounded-full animate-spin" style={{ borderColor:"#fff", borderTopColor:"transparent" }} />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          {isGoogle ? (
                            <p className="text-xs" style={{ color:"var(--text-secondary)" }}>
                              {L(lang,"Photo is synced from Google","Фото синхронизировано с Google","התמונה מסונכרנת מגוגל")}
                            </p>
                          ) : (
                            <>
                              <button type="button" onClick={() => pictureInputRef.current?.click()} disabled={pictureSaving}
                                className="w-full h-9 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all hover:opacity-90 press-sm mb-1.5"
                                style={{ background:"linear-gradient(135deg,#8B5CF6,#4F46E5)", color:"#fff" }}>
                                <Camera className="w-3.5 h-3.5" />
                                {L(lang,"Change Photo","Изменить фото","שנה תמונה")}
                              </button>
                              <p className="text-[11px] text-center" style={{ color:"var(--text-secondary)" }}>PNG / JPG · max 5 MB</p>
                            </>
                          )}
                          <input ref={pictureInputRef} type="file" accept="image/png,image/jpeg,image/jpg" className="hidden"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) {
                                if (!["image/png","image/jpeg","image/jpg"].includes(f.type)) { toast.error("PNG/JPG only"); e.target.value = ""; return; }
                                uploadPicture(f);
                              }
                              e.target.value = "";
                            }} />
                        </div>
                      </div>
                    </div>

                    {/* Name + email */}
                    <div className="px-4 py-4 border-b" style={{ borderColor:"var(--border-color)" }}>
                      {isGoogle ? (
                        <div className="flex items-center gap-3 h-11 px-3.5 rounded-xl" style={{ background:"var(--surface-2)", border:"1px solid var(--border-color)" }}>
                          <User className="w-4 h-4 flex-shrink-0" style={{ color:"var(--text-secondary)" }} />
                          <span className="flex-1 text-sm font-semibold truncate" style={{ color:"var(--text-primary)" }}>{user?.name}</span>
                        </div>
                      ) : editingName ? (
                        <div className="flex items-center gap-2">
                          <Input value={editName} onChange={(e) => setEditName(e.target.value)} maxLength={80}
                            onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setEditingName(false); }}
                            autoFocus disabled={nameSaving} placeholder={L(lang,"Your name","Ваше имя","שמך")}
                            className="h-10 text-sm rounded-xl flex-1"
                            style={{ background:"var(--surface-2)", borderColor:"rgba(139,92,246,0.4)", color:"var(--text-primary)" }} />
                          <button type="button" onClick={saveName} disabled={nameSaving}
                            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 press-sm" style={{ background:"#8B5CF6", color:"#fff" }}>
                            {nameSaving ? <div className="w-3.5 h-3.5 border-2 rounded-full animate-spin" style={{ borderColor:"#fff", borderTopColor:"transparent" }} /> : <Check className="w-4 h-4" />}
                          </button>
                          <button type="button" onClick={() => setEditingName(false)} disabled={nameSaving}
                            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 press-sm"
                            style={{ background:"var(--surface-2)", color:"var(--text-secondary)", border:"1px solid var(--border-color)" }}>
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <button type="button" onClick={() => { setEditName(user?.name || ""); setEditingName(true); }}
                          className="w-full flex items-center gap-3 h-11 px-3.5 rounded-xl transition-all hover:opacity-80 press-sm text-start"
                          style={{ background:"var(--surface-2)", border:"1px solid var(--border-color)" }}>
                          <User className="w-4 h-4 flex-shrink-0" style={{ color:"var(--text-secondary)" }} />
                          <span className="flex-1 text-sm font-semibold truncate" style={{ color:"var(--text-primary)" }}>{user?.name || t("Admin.user", lang)}</span>
                          <Edit2 className="w-3.5 h-3.5 flex-shrink-0" style={{ color:"var(--text-secondary)" }} />
                        </button>
                      )}
                      <p className="text-xs mt-2 truncate" style={{ color:"var(--text-secondary)" }}>{user?.email}</p>
                    </div>

                    {/* Password section */}
                    <div className="px-4 py-4">
                      <button type="button" onClick={() => setPwOpen(v => !v)}
                        className="w-full flex items-center gap-3 h-11 px-3.5 rounded-xl transition-all hover:opacity-80 press-sm text-start"
                        style={{ background:"var(--surface-2)", border:"1px solid var(--border-color)" }}>
                        <Lock className="w-4 h-4 flex-shrink-0" style={{ color:"var(--text-secondary)" }} />
                        <span className="flex-1 text-sm font-semibold" style={{ color:"var(--text-primary)" }}>
                          {hasPassword
                            ? L(lang,"Change Password","Изменить пароль","שנה סיסמה")
                            : L(lang,"Set Password","Установить пароль","הגדר סיסמה")}
                        </span>
                        <ChevronRight className="w-4 h-4 flex-shrink-0 transition-transform" style={{ color:"var(--text-secondary)", transform: pwOpen ? "rotate(90deg)" : "none" }} />
                      </button>

                      {pwOpen && (
                        <div className="mt-3 space-y-2">
                          {hasPassword && (
                            <input type="password" value={pwForm.current} onChange={e => setPwForm(f => ({...f, current: e.target.value}))}
                              placeholder={L(lang,"Current password","Текущий пароль","סיסמה נוכחית")}
                              className="w-full h-10 px-3 rounded-xl text-sm outline-none"
                              style={{ background:"var(--surface-2)", border:"1px solid var(--border-color)", color:"var(--text-primary)" }} />
                          )}
                          <input type="password" value={pwForm.next} onChange={e => setPwForm(f => ({...f, next: e.target.value}))}
                            placeholder={L(lang,"New password (min 8)","Новый пароль (мин 8)","סיסמה חדשה (מינ' 8)")}
                            className="w-full h-10 px-3 rounded-xl text-sm outline-none"
                            style={{ background:"var(--surface-2)", border:"1px solid var(--border-color)", color:"var(--text-primary)" }} />
                          <input type="password" value={pwForm.confirm} onChange={e => setPwForm(f => ({...f, confirm: e.target.value}))}
                            placeholder={L(lang,"Confirm password","Повторите пароль","אמת סיסמה")}
                            className="w-full h-10 px-3 rounded-xl text-sm outline-none"
                            style={{ background:"var(--surface-2)", border:"1px solid var(--border-color)", color:"var(--text-primary)" }} />
                          <button type="button" onClick={savePassword} disabled={pwSaving}
                            className="w-full h-10 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all hover:opacity-90 disabled:opacity-50"
                            style={{ background:"#8B5CF6", color:"#fff" }}>
                            {pwSaving ? <div className="w-3.5 h-3.5 border-2 rounded-full animate-spin" style={{ borderColor:"#fff", borderTopColor:"transparent" }} /> : L(lang,"Save Password","Сохранить","שמור")}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Appearance (Language + Theme) */}
              <div className="rounded-2xl border overflow-hidden card-enter card-enter-2" style={{ borderColor:"var(--border-color)", background:"var(--surface-1)" }}>
                <div className="px-4 py-3 border-b flex items-center gap-2.5" style={{ borderColor:"var(--border-color)" }}>
                  <div className="w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background:"rgba(139,92,246,0.12)" }}>
                    <Sparkles className="w-4 h-4" style={{ color:"#8B5CF6" }} />
                  </div>
                  <span className="font-bold text-sm flex-1" style={{ color:"var(--text-primary)" }}>
                    {t("DashboardV2.appearance", lang)}
                  </span>
                </div>

                {/* Language */}
                <div className="px-4 py-3 border-b" style={{ borderColor:"var(--border-color)" }}>
                  <div className="flex items-center gap-3 mb-2.5">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background:"var(--surface-2)" }}>
                      <Globe className="w-4 h-4" style={{ color:"#8B5CF6" }} />
                    </div>
                    <span className="text-sm font-medium" style={{ color:"var(--text-primary)" }}>
                      {t("Admin.language", lang)}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {[{ code:"en", label:"English" }, { code:"ru", label:"Русский" }, { code:"he", label:"עברית" }].map(({ code, label }) => {
                      const active = lang === code;
                      return (
                        <button key={code} type="button" onClick={() => { setLang(code); fetch(`${API}/user/language`, { method:"PUT", credentials:"include", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ lang: code }) }).catch(() => {}); }} data-testid={`lang-${code}`}
                          className="h-10 rounded-xl text-xs font-semibold transition-all press-sm"
                          style={{
                            background: active ? "#8B5CF6" : "var(--surface-2)",
                            border: `1px solid ${active ? "#8B5CF6" : "var(--border-color)"}`,
                            color: active ? "#fff" : "var(--text-secondary)",
                          }}>
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Theme */}
                <div className="px-4 py-3">
                  <div className="flex items-center gap-3 mb-2.5">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background:"var(--surface-2)" }}>
                      {theme === "dark" ? <Moon className="w-4 h-4" style={{ color:"#8B5CF6" }} /> : <Sun className="w-4 h-4" style={{ color:"#F77737" }} />}
                    </div>
                    <span className="text-sm font-medium" style={{ color:"var(--text-primary)" }}>
                      {t("DashboardV2.theme", lang)}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { key:"dark",  icon:<Moon className="w-3.5 h-3.5" />, label:t("DashboardV2.dark", lang) },
                      { key:"light", icon:<Sun  className="w-3.5 h-3.5" />, label:t("DashboardV2.light", lang) },
                    ].map(opt => {
                      const active = theme === opt.key;
                      return (
                        <button key={opt.key} type="button" onClick={() => applyTheme(opt.key)}
                          className="h-10 rounded-xl text-xs font-semibold transition-all flex items-center justify-center gap-1.5 press-sm"
                          style={{
                            background: active ? "#8B5CF6" : "var(--surface-2)",
                            border: `1px solid ${active ? "#8B5CF6" : "var(--border-color)"}`,
                            color: active ? "#fff" : "var(--text-secondary)",
                          }}>
                          {opt.icon}
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Email Notifications */}
              {(() => {
                const Toggle = ({ on, onClick }) => (
                  <button type="button" onClick={onClick} className="flex-shrink-0 press-sm" aria-label="toggle">
                    <div className="rounded-full relative" style={{ background: on ? "#8B5CF6" : "var(--surface-3)", width:40, height:22, transition:"background 200ms" }}>
                      <div className="absolute top-[3px] w-4 h-4 rounded-full bg-white shadow-sm"
                        style={{ left: on ? "calc(100% - 19px)" : 3, transition:"left 200ms" }} />
                    </div>
                  </button>
                );
                const rows = [
                  { key:"order_completed",  Icon:Package,      color:"#8B5CF6", label:L(lang,"Order completed","Завершение заказа","השלמת הזמנה"),          sub:L(lang,"When your order is delivered","Когда заказ выполнен","כשההזמנה בוצעה") },
                  { key:"topup_approval",   Icon:Wallet,       color:"#F77737", label:L(lang,"Balance top-up","Пополнение баланса","טעינת יתרה"),            sub:L(lang,"Approved or rejected top-ups","Пополнение одобрено или отклонено","טעינה אושרה או נדחתה") },
                  { key:"tasks",            Icon:ListChecks,   color:"#22c55e", label:L(lang,"New tasks","Новые задания","משימות חדשות"),                    sub:L(lang,"When new tasks appear on the site","Появились новые задания","כשמופיעות משימות חדשות") },
                  { key:"global_bonus",     Icon:Gift,         color:"#EC4899", label:L(lang,"Global bonus","Глобальный бонус","בונוס גלובלי"),              sub:L(lang,"Site-wide promotions and bonuses","Акции и бонусы для всех","מבצעים ובונוסים לכולם") },
                  { key:"inbox_messages",   Icon:Inbox,        color:"#0EA5E9", label:L(lang,"Site messages","Письма на сайте","הודעות באתר"),               sub:L(lang,"New messages in your inbox","Новое письмо во входящих","הודעה חדשה בתיבת הדואר") },
                  { key:"support_replies",  Icon:Headphones,   color:"#a855f7", label:L(lang,"Support replies","Ответы поддержки","תגובות תמיכה"),           sub:L(lang,"Replies to your support tickets","Ответ на ваш тикет поддержки","תגובה לפנייתך") },
                ];
                return (
                  <div className="rounded-2xl border overflow-hidden card-enter card-enter-3" style={{ borderColor:"var(--border-color)", background:"var(--surface-1)" }}>
                    {/* Header */}
                    <div className="px-4 py-3 border-b flex items-center gap-2.5" style={{ borderColor:"var(--border-color)" }}>
                      <div className="w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background:"rgba(139,92,246,0.12)" }}>
                        <Mail className="w-4 h-4" style={{ color:"#8B5CF6" }} />
                      </div>
                      <span className="font-bold text-sm flex-1" style={{ color:"var(--text-primary)" }}>
                        {L(lang,"Email notifications","Уведомления на почту","התראות במייל")}
                      </span>
                      {emailPrefsSaving && <div className="w-3.5 h-3.5 border-2 rounded-full animate-spin flex-shrink-0" style={{ borderColor:"#8B5CF6", borderTopColor:"transparent" }} />}
                    </div>

                    {/* Rows */}
                    <div className="divide-y" style={{ borderColor:"var(--border-color)" }}>
                      {rows.map(({ key, Icon, color, label, sub }) => (
                        <div key={key} className="flex items-center gap-3 px-4 py-3">
                          <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                            style={{ background:`${color}18` }}>
                            <Icon className="w-4 h-4" style={{ color }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold leading-snug" style={{ color:"var(--text-primary)" }}>{label}</p>
                            <p className="text-xs mt-0.5 leading-snug" style={{ color:"var(--text-secondary)" }}>{sub}</p>
                          </div>
                          <Toggle on={emailPrefs[key] !== false} onClick={() => toggleEmailPref(key)} />
                        </div>
                      ))}
                    </div>

                    {/* Footer */}
                    <div className="px-4 py-2.5 border-t" style={{ borderColor:"var(--border-color)" }}>
                      <p className="text-xs" style={{ color:"var(--text-secondary)" }}>
                        {L(lang,`Sent to ${user?.email||"your email"}`,`Письма на ${user?.email||"вашу почту"}`,`נשלח אל ${user?.email||"המייל שלך"}`)}
                      </p>
                    </div>
                  </div>
                );
              })()}

              {/* Sign out */}
              <div className="rounded-2xl border overflow-hidden card-enter card-enter-5" style={{ borderColor:"rgba(239,68,68,0.12)", background:"var(--surface-1)" }}>
                <button type="button" onClick={logout}
                  className="w-full flex items-center gap-3 px-4 py-4 press-row text-start">
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background:"rgba(239,68,68,0.1)" }}>
                    <LogOut className="w-4 h-4" style={{ color:"#ef4444" }} />
                  </div>
                  <span className="font-semibold text-sm" style={{ color:"#ef4444" }}>{t("DashboardV2.sign_out", lang)}</span>
                  <ChevronRight className="w-4 h-4 ms-auto flex-shrink-0" style={{ color:"rgba(239,68,68,0.5)", transform: isRTL ? "scaleX(-1)" : "none" }} />
                </button>
              </div>

            </div>
          )}

        </div>{/* end content */}

      </div>
      {showWelcome && user && <OnboardingTour onDone={async () => {
        setShowWelcome(false);
        try { await fetch(`${API}/user/tour-done`, { method: "PUT", credentials: "include" }); } catch (_) {}
        // Check survey after tour
        try {
          const r = await fetch(`${API}/onboarding/survey`, { credentials: "include" });
          if (r.ok) { const d = await r.json(); if (!d || d.status === "not_answered") setShowSurvey(true); }
        } catch (_) {}
      }} />}
      <OnboardingSurvey open={showSurvey} onClose={() => setShowSurvey(false)} />
    </>
  );
}
